import React, { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useLocation, useNavigate } from "react-router-dom";
import { callApi } from "../api/api";
import {
  Building2,
  Plus,
  Search,
  Users,
  CheckCircle2,
  Network,
  ShieldCheck,
  ArrowLeft,
  X,
  Check,
  UserCheck,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  User,
  Shield,
  UserX,
  Minus,
  Trash2,
  Lock,
  Filter,
} from "lucide-react";

const THEME = "rgb(37, 99, 235)";

// Inject global smooth keyframe animations for 2-way node drill-down / step-up transitions
if (typeof document !== "undefined" && !document.getElementById("tree-animations-style")) {
  const styleEl = document.createElement("style");
  styleEl.id = "tree-animations-style";
  styleEl.innerHTML = `
    @keyframes nodeGlideUp {
      0% {
        opacity: 0.25;
        transform: translateY(140px) scale(0.82);
      }
      65% {
        opacity: 0.95;
        transform: translateY(-5px) scale(1.05);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    @keyframes childrenCascadeDown {
      0% {
        opacity: 0;
        transform: translateY(-32px) scale(0.85);
      }
      70% {
        opacity: 0.95;
        transform: translateY(4px) scale(1.02);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    @keyframes nodeStepUp {
      0% {
        opacity: 0.25;
        transform: translateY(-140px) scale(0.82);
      }
      65% {
        opacity: 0.95;
        transform: translateY(5px) scale(1.05);
      }
      100% {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
    @keyframes pathDraw {
      0% {
        stroke-dasharray: 200;
        stroke-dashoffset: 200;
        opacity: 0;
      }
      100% {
        stroke-dashoffset: 0;
        opacity: 0.85;
      }
    }
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  `;
  document.head.appendChild(styleEl);
}

function getInitials(name) {
  if (!name) return "U";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].substring(0, 2).toUpperCase();
}

function getRoleColor(roleCode) {
  const code = (roleCode || "").toUpperCase();
  if (code.includes("CEO")) return { border: "#7c3aed", bg: "#ede9fe", color: "#6d28d9" };
  if (code.includes("VC") || code.includes("VP")) return { border: "#2563eb", bg: "#dbeafe", color: "#1d4ed8" };
  if (code.includes("SENIOR")) return { border: "#0284c7", bg: "#e0f2fe", color: "#0369a1" };
  if (code.includes("MANAGER")) return { border: "#059669", bg: "#d1fae5", color: "#047857" };
  return { border: "#64748b", bg: "#f1f5f9", color: "#334155" };
}

/* =====================================================
   VERTICAL CONNECTOR BRANCHES
===================================================== */
function CurvedBranchCanvas({ childCount, color = "#94a3b8" }) {
  return (
    <div
      style={{
        width: "2px",
        height: "24px",
        background: color,
        margin: "2px auto",
        borderRadius: "2px",
        pointerEvents: "none",
        flexShrink: 0,
      }}
    />
  );
}

/* =====================================================
   INTERACTIVE NODE GRAPH CANVAS (DRAG-TO-POSITION & DRAG-TO-CONNECT)
===================================================== */
function RoleInteractiveGraphCanvas({ hierarchyRoles, selectedRole, onSelectRole, onConnectRoles, onDisconnectRole, onDeleteRole }) {
  const containerRef = React.useRef(null);
  const [nodePositions, setNodePositions] = useState({});
  const [activeDragNodeId, setActiveDragNodeId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const [connectingSourceId, setConnectingSourceId] = useState(null);
  const [connectingSourceType, setConnectingSourceType] = useState("bottom"); // "top" (child seeking parent) | "bottom" (parent seeking child)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [hoveredTargetId, setHoveredTargetId] = useState(null);
  const [collapsedRoleIds, setCollapsedRoleIds] = useState(new Set());

  // Line selection & hover states
  const [selectedEdgeRoleId, setSelectedEdgeRoleId] = useState(null);
  const [hoveredEdgeRoleId, setHoveredEdgeRoleId] = useState(null);

  // Interactive Pan & Zoom Screen Navigation States
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [zoomScale, setZoomScale] = useState(1);

  const toggleCollapse = (roleId) => {
    setCollapsedRoleIds((prev) => {
      const next = new Set(prev);
      const isExpanding = next.has(roleId);

      if (isExpanding) {
        next.delete(roleId);

        // Accordion behavior: Auto-collapse all other sibling roles in the same row level
        const targetRoleObj = hierarchyRoles.find((r) => r.roleId === roleId);
        if (targetRoleObj) {
          const siblings = hierarchyRoles.filter(
            (r) => r.roleId !== roleId && r.parentRoleId === targetRoleObj.parentRoleId
          );
          siblings.forEach((s) => next.add(s.roleId));
        }

        // Auto-glide canvas screen down to comfortably frame newly expanded sub-roles
        const parentPos = nodePositions[roleId];
        if (parentPos && parentPos.y >= 140) {
          const targetY = Math.min(0, -(parentPos.y - 120));
          setPanOffset((p) => ({ ...p, y: targetY }));
        }
      } else {
        next.add(roleId);
        // Auto-glide canvas screen back up when shrinking lower sections
        setPanOffset((p) => ({ ...p, y: Math.min(0, p.y + 120) }));
      }
      return next;
    });
  };

  const isRoleHidden = (roleId) => {
    const roleMap = new Map();
    hierarchyRoles.forEach((r) => roleMap.set(r.roleId, r));

    let current = roleMap.get(roleId);
    while (current && current.parentRoleId) {
      if (collapsedRoleIds.has(current.parentRoleId)) return true;
      current = roleMap.get(current.parentRoleId);
    }
    return false;
  };

  const visibleRoles = hierarchyRoles.filter((r) => !isRoleHidden(r.roleId));

  // Initialize node canvas positions dynamically centered on container width & collapse lower sections by default
  useEffect(() => {
    const backendPos = {};
    let containerWidth = 1000;
    if (containerRef.current) {
      containerWidth = containerRef.current.getBoundingClientRect().width || 1000;
    }
    const centerShift = containerWidth / 2 - 500;

    hierarchyRoles.forEach((r) => {
      const layout = r.initialLayout || { x: 500, y: 50 };
      backendPos[r.roleId] = {
        x: layout.x + centerShift,
        y: layout.y,
      };
    });

    setNodePositions(backendPos);

    // Start with all parent roles collapsed by default so lower levels expand one by one
    const parentsWithChildren = hierarchyRoles.filter((r) =>
      hierarchyRoles.some((sub) => sub.parentRoleId === r.roleId)
    );
    if (parentsWithChildren.length > 0) {
      setCollapsedRoleIds(new Set(parentsWithChildren.map((r) => r.roleId)));
    }
  }, [hierarchyRoles]);

  const handlePointerMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const containerWidth = rect.width || 1000;
    const currentX = (e.clientX - rect.left - panOffset.x - containerWidth / 2) / zoomScale + containerWidth / 2;
    const currentY = (e.clientY - rect.top - panOffset.y) / zoomScale;

    setMousePos({ x: currentX, y: currentY });

    if (isPanning) {
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }

    if (activeDragNodeId) {
      setNodePositions((prev) => ({
        ...prev,
        [activeDragNodeId]: {
          x: Math.max(20, currentX - dragOffset.x),
          y: Math.max(20, currentY - dragOffset.y),
        },
      }));
    }
  };

  const handlePointerUp = () => {
    if (connectingSourceId && hoveredTargetId && connectingSourceId !== hoveredTargetId) {
      if (connectingSourceType === "top") {
        // Current node (child) connects to target node (parent/manager)
        onConnectRoles(connectingSourceId, hoveredTargetId);
      } else {
        // Current node (parent/manager) connects to target node (child/subordinate)
        onConnectRoles(hoveredTargetId, connectingSourceId);
      }
    }
    setActiveDragNodeId(null);
    setConnectingSourceId(null);
    setHoveredTargetId(null);
    setIsPanning(false);
  };

  // Calculate dynamic canvas height so expanded nodes fit comfortably
  const maxY = Math.max(540, ...visibleRoles.map((r) => (nodePositions[r.roleId]?.y || 0) + 180));

  return (
    <div
      ref={containerRef}
      onMouseDown={(e) => {
        if (e.target.closest(".no-pan")) return;
        setIsPanning(true);
        setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      }}
      onMouseMove={handlePointerMove}
      onMouseUp={handlePointerUp}
      style={{
        width: "100%",
        height: `${maxY}px`,
        background: "#ffffff",
        backgroundImage: "none",
        borderRadius: "16px",
        border: "1.5px solid #e2e8f0",
        position: "relative",
        overflow: "hidden",
        userSelect: "none",
        cursor: isPanning ? "grabbing" : "grab",
        boxShadow: "inset 0 2px 8px rgba(15,23,42,0.02)",
      }}
      data-canvas-bg="true"
    >
      {/* FLOATING PAN & ZOOM TOOLBAR */}
      <div
        className="no-pan"
        style={{
          position: "absolute",
          top: "14px",
          right: "14px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          zIndex: 20,
          background: "#ffffff",
          padding: "5px 10px",
          borderRadius: "10px",
          border: "1px solid #cbd5e1",
          boxShadow: "0 2px 10px rgba(15,23,42,0.08)",
        }}
      >
        <button
          title="Zoom In"
          onClick={() => setZoomScale((z) => Math.min(1.6, z + 0.1))}
          style={{ background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "4px 8px", fontSize: "12px", fontWeight: "800", cursor: "pointer", color: "#334155" }}
        >
          +
        </button>
        <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", minWidth: "36px", textAlign: "center" }}>
          {Math.round(zoomScale * 100)}%
        </span>
        <button
          title="Zoom Out"
          onClick={() => setZoomScale((z) => Math.max(0.6, z - 0.1))}
          style={{ background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "4px 8px", fontSize: "12px", fontWeight: "800", cursor: "pointer", color: "#334155" }}
        >
          -
        </button>
        <button
          title="Reset Screen Pan & Zoom"
          onClick={() => {
            setPanOffset({ x: 0, y: 0 });
            setZoomScale(1);
          }}
          style={{ background: "#2563eb", color: "#ffffff", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "11.5px", fontWeight: "700", cursor: "pointer", marginLeft: "4px" }}
        >
          Fit Screen
        </button>
      </div>

      {/* TRANSFORM CONTAINER FOR PANNING AND ZOOMING */}
      <div
        style={{
          width: "100%",
          height: "100%",
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomScale})`,
          transformOrigin: "50% 0",
          transition: isPanning ? "none" : "transform 0.15s ease-out",
        }}
      >
        {/* SVG CONNECTOR LINES LAYER */}
        <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 1 }}>
          {visibleRoles.map((role) => {
            if (!role.parentRoleId || !nodePositions[role.roleId] || !nodePositions[role.parentRoleId]) return null;
            if (isRoleHidden(role.roleId) || isRoleHidden(role.parentRoleId)) return null;
            const parentPos = nodePositions[role.parentRoleId];
            const childPos = nodePositions[role.roleId];

            // Upper Hierarchy / Manager ALWAYS exits Bottom Dot (parentPos.y + 56)
            const startX = parentPos.x;
            const startY = parentPos.y + 56;

            // Lower Hierarchy / Subordinate ALWAYS enters Top Dot (childPos.y)
            const endX = childPos.x;
            const endY = childPos.y;

            const dy = Math.max(20, endY - startY);
            const cp1Y = startY + dy * 0.5;
            const cp2Y = endY - dy * 0.5;
            const pathD = `M ${startX} ${startY} C ${startX} ${cp1Y}, ${endX} ${cp2Y}, ${endX} ${endY}`;

            const isEdgeSelected = selectedEdgeRoleId === role.roleId;
            const isEdgeHovered = hoveredEdgeRoleId === role.roleId;
            const showDisconnectBadge = isEdgeSelected || isEdgeHovered;

            return (
              <g
                key={`edge-${role.roleId}`}
                className="no-pan"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedEdgeRoleId(role.roleId);
                  onSelectRole(role);
                }}
                onMouseEnter={() => setHoveredEdgeRoleId(role.roleId)}
                onMouseLeave={() => setHoveredEdgeRoleId(null)}
              >
                {/* INVISIBLE THICK HIT-TEST AREA FOR EASY LINE CLICKING & HOVERING */}
                <path d={pathD} stroke="transparent" strokeWidth="16" fill="none" style={{ pointerEvents: "all" }} />

                {/* VISIBLE CONNECTOR LINE */}
                <path
                  d={pathD}
                  stroke={showDisconnectBadge ? "#2563eb" : "#64748b"}
                  strokeWidth={showDisconnectBadge ? "2.5" : "1.75"}
                  strokeOpacity={showDisconnectBadge ? "1" : "0.75"}
                  fill="none"
                  strokeLinecap="round"
                  style={{ pointerEvents: "none" }}
                />
              </g>
            );
          })}

          {/* LIVE DRAG RUBBER-BAND LINE */}
          {connectingSourceId && nodePositions[connectingSourceId] && (
            <path
              d={`M ${nodePositions[connectingSourceId].x} ${connectingSourceType === "top" ? nodePositions[connectingSourceId].y : nodePositions[connectingSourceId].y + 56} C ${nodePositions[connectingSourceId].x} ${((connectingSourceType === "top" ? nodePositions[connectingSourceId].y : nodePositions[connectingSourceId].y + 56) + mousePos.y) / 2}, ${mousePos.x} ${((connectingSourceType === "top" ? nodePositions[connectingSourceId].y : nodePositions[connectingSourceId].y + 56) + mousePos.y) / 2}, ${mousePos.x} ${mousePos.y}`}
              stroke="#7c3aed"
              strokeWidth="1.75"
              strokeDasharray="4,4"
              fill="none"
            />
          )}
        </svg>

        {/* NODE PODS LAYER */}
        {visibleRoles.map((role) => {
          const pos = nodePositions[role.roleId] || { x: 160, y: 80 };
          const isSelected = selectedRole && selectedRole.roleId === role.roleId;
          const isHoveredTarget = hoveredTargetId === role.roleId;
          const initials = getInitials(role.roleName);

          return (
            <div
              key={role.roleId}
              onMouseEnter={() => {
                if (connectingSourceId && connectingSourceId !== role.roleId) {
                  setHoveredTargetId(role.roleId);
                }
              }}
              onMouseLeave={() => {
                if (hoveredTargetId === role.roleId) setHoveredTargetId(null);
              }}
              style={{
                position: "absolute",
                left: `${pos.x}px`,
                top: `${pos.y}px`,
                transform: "translateX(-28px)",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "10px",
                zIndex: 2,
                width: "230px",
              }}
            >
              {/* ROLE CIRCLE POD */}
              <div
                className="no-pan"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectRole(role);
                }}
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  flexShrink: 0,
                  background: isHoveredTarget ? "#eff6ff" : "#ffffff",
                  border: isHoveredTarget
                    ? "2.5px dashed #2563eb"
                    : isSelected
                      ? "2.5px solid #2563eb"
                      : "1.5px solid #2563eb",
                  boxShadow: isHoveredTarget
                    ? "0 0 0 4px rgba(37, 99, 235, 0.25)"
                    : isSelected
                      ? "0 0 0 3px #eff6ff, 0 4px 12px rgba(37, 99, 235, 0.15)"
                      : "0 2px 8px rgba(15, 23, 42, 0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "50%",
                    background: "#eff6ff",
                    color: "#1d4ed8",
                    fontSize: "13.5px",
                    fontWeight: "800",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {initials}
                </div>

                {/* TOP CONNECTION POINT DOT (DRAG TO CONNECT TO MANAGER) */}
                <div
                  title="Drag line to connect to Manager / Upper Role (Top)"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setConnectingSourceType("top");
                    setConnectingSourceId(role.roleId);
                  }}
                  style={{
                    position: "absolute",
                    top: "-3.5px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#2563eb",
                    border: "1px solid #ffffff",
                    boxShadow: "0 1px 3px rgba(37, 99, 235, 0.4)",
                    cursor: "crosshair",
                    zIndex: 10,
                  }}
                />

                {/* BOTTOM CONNECTION POINT DOT (DRAG TO CONNECT TO SUBORDINATE - DISABLED FOR TEAM MEMBER) */}
                {(() => {
                  const rCode = (role.roleCode || "").toUpperCase();
                  const rName = (role.roleName || "").toLowerCase();
                  const isLowest = rCode === "TEAM_MEMBER" || rCode === "TM" || rName.includes("team member");
                  if (isLowest) return null;

                  return (
                    <div
                      title="Drag line to connect to Subordinate / Junior Role (Bottom)"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setConnectingSourceType("bottom");
                        setConnectingSourceId(role.roleId);
                      }}
                      style={{
                        position: "absolute",
                        bottom: "-3.5px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "7px",
                        height: "7px",
                        borderRadius: "50%",
                        background: "#7c3aed",
                        border: "1px solid #ffffff",
                        boxShadow: "0 1px 3px rgba(124, 58, 237, 0.4)",
                        cursor: "crosshair",
                        zIndex: 10,
                      }}
                    />
                  );
                })()}



                {/* EXPAND / SHRINK SUB-ROLES TOGGLE BADGE (+ / -) */}
                {hierarchyRoles.some((r) => r.parentRoleId === role.roleId) && (
                  <div
                    title={collapsedRoleIds.has(role.roleId) ? "Expand Lower Section (+)" : "Hide Lower Section (-)"}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCollapse(role.roleId);
                    }}
                    style={{
                      position: "absolute",
                      top: "-3px",
                      right: "-3px",
                      width: "18px",
                      height: "18px",
                      borderRadius: "50%",
                      background: collapsedRoleIds.has(role.roleId) ? "#10b981" : "#2563eb",
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: 12,
                      border: "1.5px solid #ffffff",
                      boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                    }}
                  >
                    {collapsedRoleIds.has(role.roleId) ? (
                      <Plus size={11} strokeWidth={3.5} color="#ffffff" />
                    ) : (
                      <Minus size={11} strokeWidth={3.5} color="#ffffff" />
                    )}
                  </div>
                )}
              </div>

              {/* NODE TEXT LABELS TO THE RIGHT OF CIRCLE POD */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left", minWidth: 0, flex: 1 }}>
                <div
                  title={role.roleName}
                  style={{
                    fontSize: "12.5px",
                    fontWeight: "800",
                    color: "#0f172a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "160px",
                  }}
                >
                  {role.roleName}
                </div>
                {!role.parentRoleName && (
                  <div style={{ fontSize: "10px", color: hierarchyRoles.some((r) => r.parentRoleId === role.roleId) ? "#64748b" : "#7c3aed", fontWeight: "700", marginTop: "1px" }}>
                    {hierarchyRoles.some((r) => r.parentRoleId === role.roleId) ? "Top Level Hierarchy" : "Independent Role"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =====================================================
   INTERACTIVE USER REPORTING ORG GRAPH CANVAS COMPONENT
===================================================== */
function UserInteractiveGraphCanvas({ reportingTree, selectedUser, onSelectUser, onConnectUsers, onDisconnectUser, onExpandDepts }) {
  const containerRef = React.useRef(null);
  const [nodePositions, setNodePositions] = useState({});
  const [connectingSourceId, setConnectingSourceId] = useState(null);
  const [connectingSourceType, setConnectingSourceType] = useState("bottom");
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [hoveredTargetId, setHoveredTargetId] = useState(null);
  const [collapsedUserIds, setCollapsedUserIds] = useState(new Set());

  const [selectedEdgeUserId, setSelectedEdgeUserId] = useState(null);
  const [hoveredEdgeUserId, setHoveredEdgeUserId] = useState(null);

  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [zoomScale, setZoomScale] = useState(1);

  const flatUsers = React.useMemo(() => {
    const list = [];
    const traverse = (nodes) => {
      if (!Array.isArray(nodes)) return;
      nodes.forEach((n) => {
        list.push(n);
        if (n.subordinates && n.subordinates.length > 0) {
          traverse(n.subordinates);
        }
      });
    };
    traverse(reportingTree);
    return list;
  }, [reportingTree]);

  useEffect(() => {
    let containerWidth = 1000;
    if (containerRef.current) {
      containerWidth = containerRef.current.getBoundingClientRect().width || 1000;
    }

    const calculatedPos = {};
    const initialCollapsed = new Set();
    const rootCount = reportingTree.length;
    const rootSpacing = 280;
    const startXRoot = containerWidth / 2 - ((rootCount - 1) * rootSpacing) / 2;

    const positionNode = (node, x, depth) => {
      const uId = node.userId || node.user_id;
      if (uId) {
        calculatedPos[uId] = { x, y: 50 + depth * 150 };
        calculatedPos[String(uId)] = { x, y: 50 + depth * 150 };
      }

      const children = node.subordinates || [];
      const childCount = children.length;

      if (childCount === 1) {
        positionNode(children[0], x, depth + 1);
      } else if (childCount > 1) {
        const gap = 240;
        const startChildX = x - ((childCount - 1) * gap) / 2;
        children.forEach((child, idx) => {
          const childId = child.userId || child.user_id;
          if (idx > 0 && child.subordinates && child.subordinates.length > 0) {
            initialCollapsed.add(String(childId));
          }
          positionNode(child, startChildX + idx * gap, depth + 1);
        });
      }
    };

    reportingTree.forEach((rootUser, idx) => {
      const rootX = rootCount === 1 ? containerWidth / 2 : startXRoot + idx * rootSpacing;
      positionNode(rootUser, rootX, 0);
    });

    setNodePositions(calculatedPos);
    setCollapsedUserIds((prev) => (prev.size === 0 ? initialCollapsed : prev));
  }, [flatUsers, reportingTree]);

  const toggleCollapse = (userId) => {
    if (!userId) return;
    const idStr = String(userId);
    const numId = parseInt(userId, 10);

    setCollapsedUserIds((prev) => {
      const next = new Set(prev);
      const isCurrentlyCollapsed = next.has(idStr) || (!isNaN(numId) && next.has(numId));

      if (isCurrentlyCollapsed) {
        // EXPANDING target node:
        next.delete(idStr);
        if (!isNaN(numId)) next.delete(numId);

        // ACCORDION RULE: Auto-shrink/collapse all other sibling managers in the same row level!
        const getParentId = (u) => String(u.reportingToId || u.reporting_to_id || u.parentUserId || u.parent_user_id || u.managerId || u.manager_id || "");
        const targetUserObj = flatUsers.find((u) => String(u.userId || u.user_id) === idStr);
        if (targetUserObj) {
          const targetParentId = getParentId(targetUserObj);
          const siblings = flatUsers.filter(
            (u) => String(u.userId || u.user_id) !== idStr && getParentId(u) === targetParentId
          );
          siblings.forEach((s) => {
            const sId = String(s.userId || s.user_id);
            next.add(sId);
          });
        }
      } else {
        // SHRINKING target node:
        next.add(idStr);
      }
      return next;
    });
  };

  const isUserHidden = (userId) => {
    const userMap = new Map();
    flatUsers.forEach((u) => userMap.set(String(u.userId || u.user_id), u));

    let current = userMap.get(String(userId));
    const getParentId = (u) => String(u.reportingToId || u.reporting_to_id || u.parentUserId || u.parent_user_id || u.managerId || u.manager_id || "");

    while (current) {
      const pId = getParentId(current);
      if (!pId) break;
      if (collapsedUserIds.has(pId) || collapsedUserIds.has(parseInt(pId, 10))) return true;
      current = userMap.get(pId);
    }
    return false;
  };

  const visibleUsers = flatUsers.filter((u) => !isUserHidden(u.userId));

  const handlePointerMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const containerWidth = rect.width || 1000;
    const currentX = (e.clientX - rect.left - panOffset.x - containerWidth / 2) / zoomScale + containerWidth / 2;
    const currentY = (e.clientY - rect.top - panOffset.y) / zoomScale;

    setMousePos({ x: currentX, y: currentY });

    if (isPanning) {
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
    }
  };

  const handlePointerUp = () => {
    if (connectingSourceId && hoveredTargetId && connectingSourceId !== hoveredTargetId) {
      if (connectingSourceType === "top") {
        onConnectUsers(connectingSourceId, hoveredTargetId);
      } else {
        onConnectUsers(hoveredTargetId, connectingSourceId);
      }
    }
    setConnectingSourceId(null);
    setHoveredTargetId(null);
    setIsPanning(false);
  };
  const maxY = Math.max(750, ...visibleUsers.map((u) => {
    const idStr = String(u.userId || u.user_id || "");
    return (nodePositions[idStr]?.y || 0) + 250;
  }));

  return (
    <div
      ref={containerRef}
      onMouseDown={(e) => {
        if (e.target.closest(".no-pan")) return;
        setIsPanning(true);
        setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      }}
      onMouseMove={handlePointerMove}
      onMouseUp={handlePointerUp}
      style={{
        width: "100%",
        height: `${maxY}px`,
        position: "relative",
        overflow: "hidden",
        background: "#ffffff",
        backgroundImage: "none",
        borderRadius: "16px",
        border: "1.5px solid #cbd5e1",
        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)",
        userSelect: "none",
        cursor: isPanning ? "grabbing" : "grab",
      }}
    >
      {/* MINIMAP / QUICK NAV CONTROLS */}
      <div
        className="no-pan"
        style={{
          position: "absolute",
          top: "14px",
          right: "14px",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          zIndex: 20,
          background: "#ffffff",
          padding: "5px 10px",
          borderRadius: "10px",
          border: "1px solid #cbd5e1",
          boxShadow: "0 2px 10px rgba(15,23,42,0.08)",
        }}
      >
        <button
          title="Zoom In"
          onClick={() => setZoomScale((z) => Math.min(1.6, z + 0.1))}
          style={{ background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "4px 8px", fontSize: "12px", fontWeight: "800", cursor: "pointer", color: "#334155" }}
        >
          +
        </button>
        <span style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", minWidth: "36px", textAlign: "center" }}>
          {Math.round(zoomScale * 100)}%
        </span>
        <button
          title="Zoom Out"
          onClick={() => setZoomScale((z) => Math.max(0.6, z - 0.1))}
          style={{ background: "#f1f5f9", border: "1px solid #cbd5e1", borderRadius: "6px", padding: "4px 8px", fontSize: "12px", fontWeight: "800", cursor: "pointer", color: "#334155" }}
        >
          -
        </button>
        <button
          title="Reset Screen Pan & Zoom"
          onClick={() => {
            setPanOffset({ x: 0, y: 0 });
            setZoomScale(1);
          }}
          style={{ background: "#2563eb", color: "#ffffff", border: "none", borderRadius: "6px", padding: "4px 10px", fontSize: "11.5px", fontWeight: "700", cursor: "pointer", marginLeft: "4px" }}
        >
          Fit Screen
        </button>
      </div>

      {/* TRANSFORM CONTAINER FOR PANNING AND ZOOMING */}
      <div
        style={{
          width: "100%",
          height: "100%",
          transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomScale})`,
          transformOrigin: "50% 0",
          transition: isPanning ? "none" : "transform 0.15s ease-out",
        }}
      >
        {/* SVG CONNECTOR LINES LAYER */}
        <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: `${maxY + 500}px`, pointerEvents: "none", zIndex: 1, overflow: "visible" }}>
          {visibleUsers.map((user) => {
            const uId = String(user.userId || user.user_id || "");
            const rId = String(user.reportingToId || user.reporting_to_id || user.parentUserId || user.parent_user_id || user.managerId || user.manager_id || "");

            if (!rId || !nodePositions[uId] || !nodePositions[rId]) return null;
            if (isUserHidden(uId) || isUserHidden(rId)) return null;

            const parentPos = nodePositions[rId];
            const childPos = nodePositions[uId];

            const startX = parentPos.x;
            const startY = parentPos.y + 56;
            const endX = childPos.x;
            const endY = childPos.y;

            const dy = Math.max(20, endY - startY);
            const cp1Y = startY + dy * 0.5;
            const cp2Y = endY - dy * 0.5;
            const pathD = `M ${startX} ${startY} C ${startX} ${cp1Y}, ${endX} ${cp2Y}, ${endX} ${endY}`;
            const midX = (startX + endX) / 2;
            const midY = (startY + endY) / 2;

            const isEdgeSelected = String(selectedEdgeUserId) === uId;
            const isEdgeHovered = String(hoveredEdgeUserId) === uId;
            const showDisconnectBadge = isEdgeSelected || isEdgeHovered;

            return (
              <g
                key={`edge-${user.userId}`}
                className="no-pan"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedEdgeUserId(user.userId);
                  onSelectUser(user);
                }}
                onMouseEnter={() => setHoveredEdgeUserId(user.userId)}
                onMouseLeave={() => setHoveredEdgeUserId(null)}
              >
                <path d={pathD} stroke="transparent" strokeWidth="16" fill="none" style={{ pointerEvents: "all" }} />
                <path
                  d={pathD}
                  stroke={showDisconnectBadge ? "#ef4444" : "#64748b"}
                  strokeWidth={showDisconnectBadge ? "2.5" : "1.75"}
                  strokeOpacity={showDisconnectBadge ? "1" : "0.75"}
                  fill="none"
                  strokeLinecap="round"
                  style={{ pointerEvents: "none" }}
                />

                {showDisconnectBadge && (
                  <g
                    title="Click to disconnect this user reporting line"
                    style={{ cursor: "pointer", pointerEvents: "all" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDisconnectUser(user.userId);
                    }}
                  >
                    <circle cx={midX} cy={midY} r="9.5" fill="#ef4444" stroke="#ffffff" strokeWidth="1.5" style={{ filter: "drop-shadow(0px 2px 5px rgba(239,68,68,0.45))" }} />
                    <text x={midX} y={midY + 3.5} textAnchor="middle" fontSize="12" fontWeight="900" fill="#ffffff">×</text>
                  </g>
                )}
              </g>
            );
          })}

          {/* LIVE DRAG RUBBER-BAND LINE */}
          {connectingSourceId && nodePositions[connectingSourceId] && (
            <path
              d={`M ${nodePositions[connectingSourceId].x} ${connectingSourceType === "top" ? nodePositions[connectingSourceId].y : nodePositions[connectingSourceId].y + 56} C ${nodePositions[connectingSourceId].x} ${((connectingSourceType === "top" ? nodePositions[connectingSourceId].y : nodePositions[connectingSourceId].y + 56) + mousePos.y) / 2}, ${mousePos.x} ${((connectingSourceType === "top" ? nodePositions[connectingSourceId].y : nodePositions[connectingSourceId].y + 56) + mousePos.y) / 2}, ${mousePos.x} ${mousePos.y}`}
              stroke="#7c3aed"
              strokeWidth="1.75"
              strokeDasharray="4,4"
              fill="none"
            />
          )}
        </svg>

        {/* NODE PODS LAYER */}
        {visibleUsers.map((user) => {
          const pos = nodePositions[user.userId] || { x: 500, y: 50 };
          const isSelected = selectedUser && selectedUser.userId === user.userId;
          const isHoveredTarget = hoveredTargetId === user.userId;
          const initials = getInitials(`${user.firstName} ${user.lastName}`);
          const roleTheme = getRoleColor(user.roleCode);

          return (
            <div
              key={user.userId}
              onMouseEnter={() => {
                if (connectingSourceId && connectingSourceId !== user.userId) {
                  setHoveredTargetId(user.userId);
                }
              }}
              onMouseLeave={() => {
                if (hoveredTargetId === user.userId) setHoveredTargetId(null);
              }}
              style={{
                position: "absolute",
                left: `${pos.x}px`,
                top: `${pos.y}px`,
                transform: "translateX(-28px)",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "10px",
                zIndex: 2,
                width: "230px",
              }}
            >
              {/* USER CIRCLE POD */}
              <div
                className="no-pan"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectUser(user);
                }}
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  flexShrink: 0,
                  background: isHoveredTarget ? "#eff6ff" : "#ffffff",
                  border: isHoveredTarget
                    ? "2.5px dashed #2563eb"
                    : isSelected
                      ? `2.5px solid ${roleTheme.border}`
                      : `1.5px solid ${roleTheme.border}`,
                  boxShadow: isHoveredTarget
                    ? "0 0 0 4px rgba(37, 99, 235, 0.25)"
                    : isSelected
                      ? `0 0 0 3px ${roleTheme.bg}, 0 4px 12px rgba(37, 99, 235, 0.15)`
                      : "0 2px 8px rgba(15, 23, 42, 0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    borderRadius: "50%",
                    background: roleTheme.bg,
                    color: roleTheme.color,
                    fontSize: "13.5px",
                    fontWeight: "800",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {initials}
                </div>

                {/* TOP CONNECTION POINT DOT (DRAG TO CONNECT TO MANAGER USER) */}
                <div
                  title="Drag line to connect to Manager User (Top)"
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setConnectingSourceType("top");
                    setConnectingSourceId(user.userId);
                  }}
                  style={{
                    position: "absolute",
                    top: "-3.5px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#2563eb",
                    border: "1px solid #ffffff",
                    boxShadow: "0 1px 3px rgba(37, 99, 235, 0.4)",
                    cursor: "crosshair",
                    zIndex: 10,
                  }}
                />

                {/* BOTTOM CONNECTION POINT DOT (DRAG TO CONNECT TO SUBORDINATE USER - HIDDEN FOR TEAM MEMBERS) */}
                {!(String(user.roleCode || "").toUpperCase() === "TEAM_MEMBER" || String(user.roleName || "").toUpperCase().includes("TEAM MEMBER")) && (
                  <div
                    title="Drag line to connect to Subordinate User (Bottom)"
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setConnectingSourceType("bottom");
                      setConnectingSourceId(user.userId);
                    }}
                    style={{
                      position: "absolute",
                      bottom: "-3.5px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "7px",
                      height: "7px",
                      borderRadius: "50%",
                      background: "#7c3aed",
                      border: "1px solid #ffffff",
                      boxShadow: "0 1px 3px rgba(124, 58, 237, 0.4)",
                      cursor: "crosshair",
                      zIndex: 10,
                    }}
                  />
                )}

                {/* EXPAND / SHRINK SUBORDINATES TOGGLE BADGE (+ / - ONLY IF USER HAS SUBORDINATES) */}
                {(() => {
                  const uIdStr = String(user.userId || user.user_id || "");

                  // Query all direct subordinates of this user in reporting tree and flat users
                  const rawSubs = (user.subordinates || []).filter((s) => s && (s.userId || s.user_id));
                  const flatSubs = flatUsers.filter((u) => {
                    const rId = String(u.reportingToId || u.reporting_to_id || u.parentUserId || u.parent_user_id || u.managerId || u.manager_id || "");
                    const childId = String(u.userId || u.user_id || "");
                    return rId === uIdStr && childId !== uIdStr;
                  });

                  const subMap = new Map();
                  rawSubs.forEach((s) => subMap.set(String(s.userId || s.user_id), s));
                  flatSubs.forEach((s) => subMap.set(String(s.userId || s.user_id), s));
                  const validSubs = Array.from(subMap.values());

                  if (validSubs.length === 0) return null;

                  const isCollapsed = collapsedUserIds.has(uIdStr) || collapsedUserIds.has(user.userId) || collapsedUserIds.has(user.user_id);

                  return (
                    <div
                      title={isCollapsed ? "Expand Subordinates (+)" : "Hide Subordinates (-)"}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCollapse(user.userId || user.user_id);
                      }}
                      style={{
                        position: "absolute",
                        top: "-3px",
                        right: "-3px",
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        background: isCollapsed ? "#10b981" : "#2563eb",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        zIndex: 12,
                        border: "1.5px solid #ffffff",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                      }}
                    >
                      {isCollapsed ? (
                        <Plus size={11} strokeWidth={3.5} color="#ffffff" />
                      ) : (
                        <Minus size={11} strokeWidth={3.5} color="#ffffff" />
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* USER TEXT LABELS TO THE RIGHT OF CIRCLE POD */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left", minWidth: 0, flex: 1 }}>
                <div
                  title={`${user.firstName} ${user.lastName}`}
                  style={{
                    fontSize: "12.5px",
                    fontWeight: "800",
                    color: "#0f172a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "160px",
                  }}
                >
                  {user.firstName} {user.lastName}
                </div>
                <div
                  title={user.roleName || "Team Member"}
                  style={{
                    fontSize: "11px",
                    color: roleTheme.color,
                    fontWeight: "700",
                    marginTop: "1px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "160px",
                  }}
                >
                  {user.roleName || "Team Member"}
                </div>
                {(() => {
                  const depts = user.departments || [];
                  if (depts.length === 0) return null;
                  if (depts.length === 1) {
                    return (
                      <div
                        title={depts[0].departmentName}
                        style={{
                          fontSize: "10px",
                          color: "#64748b",
                          fontWeight: "600",
                          marginTop: "1px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "160px",
                        }}
                      >
                        {depts[0].departmentName}
                      </div>
                    );
                  }
                  const firstName = depts[0].departmentName;
                  const extraCount = depts.length - 1;

                  return (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectUser(user);
                      }}
                      title={`${firstName} + ${extraCount} more`}
                      style={{
                        fontSize: "10px",
                        color: "#475569",
                        marginTop: "2px",
                        cursor: "pointer",
                        background: "#eff6ff",
                        padding: "2px 8px",
                        borderRadius: "6px",
                        border: "1px solid #bfdbfe",
                        fontWeight: "700",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        maxWidth: "160px",
                      }}
                    >
                      <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "90px" }}>{firstName}</span>
                      <span style={{ color: "#2563eb", fontWeight: "800" }}>+{extraCount} more</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =====================================================
   MAIN DEPARTMENT & HIERARCHY PAGE COMPONENT
===================================================== */
export default function Department() {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const location = useLocation();
  const [viewMode, setViewMode] = useState("menu"); // "menu" | "directory" | "hierarchy" | "roles" | "role-users"

  // Reset to "menu" (Departments & Hierarchy landing page) whenever user clicks sidebar link
  useEffect(() => {
    setViewMode("menu");
  }, [location.key]);

  const [departments, setDepartments] = useState([]);
  const [reportingTree, setReportingTree] = useState([]);
  const [hierarchyRoles, setHierarchyRoles] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [deptFilter, setDeptFilter] = useState("all");
  // eslint-disable-next-line no-unused-vars
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [animDirection, setAnimDirection] = useState("down");

  // Interactive Circular Node Selection State
  const [selectedNode, setSelectedNode] = useState(null);
  const [isDeptsExpanded, setIsDeptsExpanded] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [draggingRoleId, setDraggingRoleId] = useState(null);
  const [connectingSourceRoleId, setConnectingSourceRoleId] = useState(null);
  const [dragOverRoleId, setDragOverRoleId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  // eslint-disable-next-line no-unused-vars
  const [dragCurrentPos, setDragCurrentPos] = useState({ x: 0, y: 0 });
  const [expandedNodes, setExpandedNodes] = useState({});

  // Delete Independent Role Modal State
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [isDeletingRole, setIsDeletingRole] = useState(false);

  // Modals State
  const [showAddDeptModal, setShowAddDeptModal] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const [newDeptCode, setNewDeptCode] = useState("");
  const [newDeptDesc, setNewDeptDesc] = useState("");

  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleCode, setNewRoleCode] = useState("");
  const [newRoleParentId, setNewRoleParentId] = useState("");
  const [newRoleMultiDept, setNewRoleMultiDept] = useState(true);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [isAssignAccessOnly, setIsAssignAccessOnly] = useState(false);
  const [assignUserId, setAssignUserId] = useState("");
  const [assignRoleId, setAssignRoleId] = useState("");
  const [assignDeptIds, setAssignDeptIds] = useState([]);
  const [assignReportingToId, setAssignReportingToId] = useState("");
  const [candidateSuperiorUsers, setCandidateSuperiorUsers] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingCandidateSuperiors, setLoadingCandidateSuperiors] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [showDeptConflictModal, setShowDeptConflictModal] = useState(false);
  const [deptConflictData, setDeptConflictData] = useState(null);
  const [checkingDeptId, setCheckingDeptId] = useState(null);
  const [openConflictDropdownIdx, setOpenConflictDropdownIdx] = useState(null);
  const [openUserDeptDropdownId, setOpenUserDeptDropdownId] = useState(null);
  const [isDeptFilterDropdownOpen, setIsDeptFilterDropdownOpen] = useState(false);

  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceOldUser, setReplaceOldUser] = useState(null);
  const [replaceNewUserId, setReplaceNewUserId] = useState("");
  const [isReplaceUserDropdownOpen, setIsReplaceUserDropdownOpen] = useState(false);
  const [replaceUserSearchQuery, setReplaceUserSearchQuery] = useState("");

  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);

  const [showRoleUserAssignModal, setShowRoleUserAssignModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [targetAssignRole, setTargetAssignRole] = useState(null);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [roleUserSearchQuery, setRoleUserSearchQuery] = useState("");
  const [selectedRoleTab, setSelectedRoleTab] = useState(null);
  const [isParentRoleDropdownOpen, setIsParentRoleDropdownOpen] = useState(false);
  const [isRoleUserAssignDropdownOpen, setIsRoleUserAssignDropdownOpen] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [loadingAssignableUsers, setLoadingAssignableUsers] = useState(false);

  const [subordinateUsers, setSubordinateUsers] = useState([]);
  const [managerDepartments, setManagerDepartments] = useState([]);
  const [subordinateRoleInfo, setSubordinateRoleInfo] = useState(null);
  const [loadingSubordinateUsers, setLoadingSubordinateUsers] = useState(false);

  const [isAssignUserDropdownOpen, setIsAssignUserDropdownOpen] = useState(false);
  const [assignUserSearchQuery, setAssignUserSearchQuery] = useState("");
  const [isAssignRoleDropdownOpen, setIsAssignRoleDropdownOpen] = useState(false);
  const [isAssignReportingToDropdownOpen, setIsAssignReportingToDropdownOpen] = useState(false);
  const [assignReportingToSearchQuery, setAssignReportingToSearchQuery] = useState("");

  const [showDeptMembersModal, setShowDeptMembersModal] = useState(false);
  const [selectedDeptForMembers, setSelectedDeptForMembers] = useState(null);
  const [deptMembersList, setDeptMembersList] = useState([]);
  const [loadingDeptMembers, setLoadingDeptMembers] = useState(false);
  const [deptMemberSearchQuery, setDeptMemberSearchQuery] = useState("");

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const resetAssignModalState = useCallback(() => {
    setAssignUserId("");
    setAssignRoleId("");
    setAssignDeptIds([]);
    setAssignReportingToId("");
    setAssignUserSearchQuery("");
    setAssignReportingToSearchQuery("");
    setIsAssignUserDropdownOpen(false);
    setIsAssignRoleDropdownOpen(false);
    setIsAssignReportingToDropdownOpen(false);
    setManagerDepartments([]);
    setSubordinateRoleInfo(null);
    setIsAssignAccessOnly(false);
    setSelectedNode(null);
  }, []);

  const handleCloseAssignModal = useCallback(() => {
    setShowAssignModal(false);
    resetAssignModalState();
  }, [resetAssignModalState]);

  const handleOpenDeptMembersModal = async (dept) => {
    setSelectedDeptForMembers(dept);
    setShowDeptMembersModal(true);
    setDeptMemberSearchQuery("");
    setLoadingDeptMembers(true);
    try {
      const deptId = dept.departmentId || dept.department_id;
      const res = await callApi(instance, accounts, "GET", `/api/departments/${deptId}/members`);
      const members = res?.data?.data || res?.data || res || [];
      setDeptMembersList(Array.isArray(members) ? members : []);
    } catch (err) {
      console.error("Failed to load department members:", err);
      showToast(err.message || "Failed to load department members", "error");
      setDeptMembersList([]);
    } finally {
      setLoadingDeptMembers(false);
    }
  };

  // Canvas-level real-time pointer tracking for 100% pixel-perfect line drawing
  useEffect(() => {
    if (!draggingRoleId && !connectingSourceRoleId) return;

    const handlePointerMove = (e) => {
      const canvasEl = document.querySelector(".tree-canvas-container");
      if (canvasEl) {
        const rect = canvasEl.getBoundingClientRect();
        setDragCurrentPos({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      } else {
        setDragCurrentPos({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUpGlobal = () => {
      if (connectingSourceRoleId) {
        setConnectingSourceRoleId(null);
        setDragOverRoleId(null);
      }
    };

    window.addEventListener("dragover", handlePointerMove);
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handleMouseUpGlobal);

    return () => {
      window.removeEventListener("dragover", handlePointerMove);
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handleMouseUpGlobal);
    };
  }, [draggingRoleId, connectingSourceRoleId]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        callApi(instance, accounts, "GET", "/api/departments"),
        callApi(instance, accounts, "GET", "/api/departments/reporting-tree"),
        callApi(instance, accounts, "GET", "/api/departments/roles"),
        callApi(instance, accounts, "GET", "/api/admin/users"),
      ]);

      const [deptResult, treeResult, rolesResult, usersResult] = results;

      if (deptResult.status === "fulfilled") {
        const d = deptResult.value?.data?.data || deptResult.value?.data || [];
        setDepartments(Array.isArray(d) ? d : []);
      }
      if (treeResult.status === "fulfilled") {
        const t = treeResult.value?.data?.data || treeResult.value?.data || [];
        setReportingTree(Array.isArray(t) ? t : []);
      }
      let fetchedRoles = [];
      if (rolesResult.status === "fulfilled") {
        const r = rolesResult.value?.data?.data || rolesResult.value?.data || [];
        fetchedRoles = Array.isArray(r) ? r : [];
        setHierarchyRoles(fetchedRoles);
      }
      if (usersResult.status === "fulfilled") {
        const u = usersResult.value?.data?.data || usersResult.value?.data || [];
        const rawList = Array.isArray(u) ? u : [];
        const enrichedUsers = rawList.map((usr) => {
          const usrId = String(usr.userId || usr.user_id);
          const foundRole = fetchedRoles.find((role) =>
            (role.users || []).some((m) => String(m.userId || m.user_id) === usrId)
          );
          return {
            ...usr,
            currentRoleName: usr.currentRoleName || usr.current_role_name || (foundRole ? foundRole.roleName : null),
          };
        });

        setAllUsers(enrichedUsers);

        // Synchronize active selectedNode in docked detail panel in real time!
        setSelectedNode((prev) => {
          if (!prev) return null;
          const pId = String(prev.userId || prev.user_id);
          const foundInTree = (treeResult.value?.data?.data || treeResult.value?.data || []).find(
            (node) => String(node.userId || node.user_id) === pId
          );
          if (foundInTree) return { ...prev, ...foundInTree };
          return prev;
        });
      }
    } catch (err) {
      console.error("Failed to load department data:", err);
      showToast("Failed to fetch department data.", "error");
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, showToast]);

  // Fetch initial data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load backend SQL-driven unmapped assignable users when showAssignModal or showRoleUserAssignModal opens
  useEffect(() => {
    if (showAssignModal || showRoleUserAssignModal) {
      let isMounted = true;
      setLoadingAssignableUsers(true);
      const roleParam = targetAssignRole ? `targetRoleId=${targetAssignRole.roleId}` : "";
      const deptQuery = deptFilter && deptFilter !== "all" ? `&departmentId=${deptFilter}` : "";
      callApi(instance, accounts, "GET", `/api/departments/assignable-users?${roleParam}${deptQuery}`)
        .then((res) => {
          if (isMounted) {
            const arr = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
            setAssignableUsers(arr);
          }
        })
        .catch((err) => {
          console.error("Failed to load assignable users from backend", err);
          if (isMounted) setAssignableUsers([]);
        })
        .finally(() => {
          if (isMounted) setLoadingAssignableUsers(false);
        });
      return () => { isMounted = false; };
    }
  }, [showAssignModal, showRoleUserAssignModal, targetAssignRole, deptFilter, instance, accounts]);

  // Load backend-driven assignable subordinates when adding a member under a manager
  useEffect(() => {
    if (showAssignModal && assignReportingToId) {
      let isMounted = true;
      setLoadingSubordinateUsers(true);
      callApi(instance, accounts, "GET", `/api/departments/assignable-subordinates?parentUserId=${assignReportingToId}`)
        .then((res) => {
          if (isMounted) {
            const data = res?.data?.data || res?.data || {};
            const userList = Array.isArray(data.users) ? data.users : [];
            const mgrDepts = Array.isArray(data.parentManagerDepartments) ? data.parentManagerDepartments : [];
            setSubordinateUsers(userList);
            setManagerDepartments(mgrDepts);
            const subRole = data.defaultSubordinateRole || null;
            setSubordinateRoleInfo(subRole);
            if (subRole) {
              setAssignRoleId(String(subRole.role_id));
            }
          }
        })
        .catch((err) => {
          console.error("Failed to load assignable subordinates:", err);
          if (isMounted) {
            setSubordinateUsers([]);
            setManagerDepartments([]);
            setSubordinateRoleInfo(null);
          }
        })
        .finally(() => {
          if (isMounted) setLoadingSubordinateUsers(false);
        });
      return () => { isMounted = false; };
    } else {
      setSubordinateUsers([]);
      setManagerDepartments([]);
      setSubordinateRoleInfo(null);
    }
  }, [showAssignModal, assignReportingToId, instance, accounts]);

  // Load backend-driven candidate superior managers from SQL DB when assignUserId or assignRoleId changes
  useEffect(() => {
    if (showAssignModal) {
      let isMounted = true;
      setLoadingCandidateSuperiors(true);
      const queryParams = `childUserId=${assignUserId || ""}&childRoleId=${assignRoleId || ""}`;
      callApi(instance, accounts, "GET", `/api/departments/assignable-superiors?${queryParams}`)
        .then((res) => {
          if (isMounted) {
            const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
            setCandidateSuperiorUsers(list);
          }
        })
        .catch((err) => {
          console.error("Failed to load assignable superiors from backend SQL DB:", err);
          if (isMounted) setCandidateSuperiorUsers([]);
        })
        .finally(() => {
          if (isMounted) setLoadingCandidateSuperiors(false);
        });
      return () => { isMounted = false; };
    } else {
      setCandidateSuperiorUsers([]);
    }
  }, [showAssignModal, assignUserId, assignRoleId, instance, accounts]);

  // Auto-populate & lock user's assigned role when assignUserId / assignReportingToId changes in Map User Hierarchy modal
  useEffect(() => {
    if (showAssignModal) {
      if (!assignReportingToId) {
        // Top Level (No Parent) selected: Auto-assign Top Root Role (e.g. Project Manager)
        const topRootRole = hierarchyRoles.find(
          (r) => !r.parentRoleId && !r.parent_role_id
        ) || hierarchyRoles[0];
        if (topRootRole) {
          setAssignRoleId(String(topRootRole.roleId || topRootRole.role_id));
        }
      } else if (assignUserId) {
        const selectedUserObj = allUsers.find(
          (u) => String(u.userId || u.user_id) === String(assignUserId)
        );
        const userAssignedRole = hierarchyRoles.find(
          (r) =>
            (r.users || []).some((m) => String(m.userId || m.user_id) === String(assignUserId)) ||
            (selectedUserObj?.currentRoleName && r.roleName === selectedUserObj.currentRoleName)
        );
        if (userAssignedRole) {
          setAssignRoleId(String(userAssignedRole.roleId || userAssignedRole.role_id));
        }
      }
    }
  }, [showAssignModal, assignUserId, assignReportingToId, allUsers, hierarchyRoles]);

  // Auto-trim departments to max 1 item if active role is 1-Dept Only (e.g. Team Member)
  useEffect(() => {
    if (showAssignModal) {
      const activeRoleObj =
        hierarchyRoles.find((r) => String(r.roleId) === String(assignRoleId)) || subordinateRoleInfo;
      const isMultiDeptAllowed = activeRoleObj
        ? Boolean(activeRoleObj.allowMultiDept ?? activeRoleObj.allow_multi_dept)
        : true;

      if (!isMultiDeptAllowed && assignDeptIds.length > 1) {
        setAssignDeptIds([assignDeptIds[0]]);
      }
    }
  }, [showAssignModal, assignRoleId, subordinateRoleInfo, hierarchyRoles, assignDeptIds]);

  // eslint-disable-next-line no-unused-vars
  const handleToggleNodeSelect = (node) => {
    const hasChildren = (node.subordinates || []).length > 0;
    const isCurrentlyExpanded = Boolean(expandedNodes[node.userId]);

    if (hasChildren) {
      if (isCurrentlyExpanded) {
        if (node.reportingToId) {
          setFocusedNodeId(node.reportingToId);
        } else {
          setFocusedNodeId(null);
        }
        setSelectedNode(node);
        setExpandedNodes((prev) => ({ ...prev, [node.userId]: false }));
      } else {
        // EXPAND / DRILL DOWN LOGIC (+ button): Set animation direction DOWN
        setAnimDirection("down");
        setFocusedNodeId(node.userId);
        setSelectedNode(node);
        setExpandedNodes((prev) => ({ ...prev, [node.userId]: true }));
      }
    } else {
      // LEAF NODE (0 Children): Do NOT move to top! Simply select node and open detail popover card!
      setSelectedNode((prev) => (prev && prev.userId === node.userId ? null : node));
    }
  };

  const handleCreateDept = async (e) => {
    e.preventDefault();
    if (!newDeptName.trim()) return showToast("Department name required", "error");
    try {
      setSubmitting(true);
      await callApi(instance, accounts, "POST", "/api/departments", {
        name: newDeptName.trim(),
        code: newDeptCode.trim() || newDeptName.trim().replace(/\s+/g, "_").toUpperCase(),
        description: newDeptDesc.trim(),
      });
      showToast(`Department "${newDeptName}" created!`);
      setShowAddDeptModal(false);
      setNewDeptName("");
      setNewDeptCode("");
      setNewDeptDesc("");
      fetchData();
    } catch (err) {
      showToast(err?.data?.message || err.message || "Failed to create department", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) return showToast("Role name is required", "error");
    const code = newRoleCode.trim() || newRoleName.trim().replace(/\s+/g, "_").toUpperCase();
    if (!code) return showToast("Role Code is mandatory and must be unique!", "error");

    try {
      setSubmitting(true);
      if (editingRoleId) {
        await callApi(instance, accounts, "PUT", `/api/departments/roles/${editingRoleId}`, {
          roleName: newRoleName.trim(),
          roleCode: code,
          parentRoleId: newRoleParentId ? parseInt(newRoleParentId, 10) : null,
          allowMultiDept: newRoleMultiDept ? 1 : 0,
        });
        showToast(`Hierarchy role "${newRoleName}" updated!`);
      } else {
        await callApi(instance, accounts, "POST", "/api/departments/roles", {
          roleName: newRoleName.trim(),
          roleCode: code,
          parentRoleId: newRoleParentId ? parseInt(newRoleParentId, 10) : null,
          allowMultiDept: newRoleMultiDept ? 1 : 0,
        });
        showToast(`Hierarchy role "${newRoleName}" created!`);
      }
      setShowRolesModal(false);
      setShowAddRoleModal(false);
      setEditingRoleId(null);
      setNewRoleName("");
      setNewRoleCode("");
      setNewRoleParentId("");
      setNewRoleMultiDept(true);
      fetchData();
    } catch (err) {
      showToast(err?.data?.message || err.message || "Failed to save role", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDropRoleConnect = async (draggedId, targetParentId) => {
    setDragOverRoleId(null);
    setDraggingRoleId(null);

    if (!draggedId || String(draggedId) === String(targetParentId)) return;

    const draggedRole = hierarchyRoles.find((r) => String(r.roleId) === String(draggedId));
    const targetRole = hierarchyRoles.find((r) => String(r.roleId) === String(targetParentId));

    if (targetRole) {
      const pCode = (targetRole.roleCode || "").toUpperCase();
      const pName = (targetRole.roleName || "").toLowerCase();
      if (pCode === "TEAM_MEMBER" || pCode === "TM" || pName.includes("team member")) {
        return showToast(`'${targetRole.roleName}' is the lowest hierarchy level. Subordinate roles cannot report to Team Member.`, "error");
      }
    }

    try {
      await callApi(instance, accounts, "PUT", `/api/departments/roles/${draggedId}/parent`, {
        parentRoleId: targetParentId ? parseInt(targetParentId, 10) : null,
      });
      showToast(`Connected "${draggedRole?.roleName || "Role"}" -> Reports to "${targetRole?.roleName || "Role"}"!`);
      fetchData();
    } catch (err) {
      showToast(err?.data?.message || err.message || "Failed to connect roles", "error");
    }
  };

  const executeAssignUserSubmit = async (uId, rId, dIds, rToId) => {
    try {
      setSubmitting(true);
      await callApi(instance, accounts, "POST", "/api/departments/assign-user", {
        userId: uId,
        roleId: isAssignAccessOnly ? null : rId,
        departmentIds: dIds,
        reportingToId: rToId || null,
      });

      showToast("User hierarchy & department access updated!");
      setShowAssignModal(false);
      setShowDeptConflictModal(false);
      setDeptConflictData(null);
      resetAssignModalState();
      if (rToId) {
        setExpandedNodes((prev) => ({ ...prev, [rToId]: true }));
      }
      fetchData();
    } catch (err) {
      showToast(err?.data?.message || err.message || "Failed to update user hierarchy", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignUserSubmit = async (e) => {
    e.preventDefault();
    if (!assignUserId) {
      return showToast("User selection is required.", "error");
    }

    try {
      // Backend-driven conflict check API call (< 2ms SQL execution)
      const checkRes = await callApi(instance, accounts, "POST", "/api/departments/check-conflict", {
        userId: assignUserId,
        departmentIds: assignDeptIds,
      });

      const conflictData = checkRes?.data?.data || checkRes?.data;
      if (conflictData && conflictData.hasConflict && conflictData.affectedSubordinates?.length > 0) {
        const targetUserObj = allUsers.find((u) => String(u.userId || u.user_id) === String(assignUserId));
        setDeptConflictData({
          managerUser: targetUserObj,
          newDeptIds: assignDeptIds,
          previousDeptIds: [...assignDeptIds],
          affectedSubordinates: conflictData.affectedSubordinates,
        });
        setShowAssignModal(false);
        setShowDeptConflictModal(true);
        return;
      }

      await executeAssignUserSubmit(assignUserId, assignRoleId, assignDeptIds, assignReportingToId);
    } catch (err) {
      showToast(err?.data?.message || err?.message || "Failed to process user hierarchy submission", "error");
    }
  };

  const handleConfirmReplaceUser = async () => {
    if (!replaceOldUser || !replaceNewUserId) {
      showToast("Please select a replacement user.", "error");
      return;
    }

    try {
      setSubmitting(true);
      const oldId = replaceOldUser.userId || replaceOldUser.user_id;
      await callApi(instance, accounts, "POST", "/api/departments/replace-user", {
        oldUserId: oldId,
        newUserId: replaceNewUserId,
      });

      showToast("User replaced successfully in hierarchy!", "success");
      setShowReplaceModal(false);
      setReplaceOldUser(null);
      setReplaceNewUserId("");
      setSelectedNode(null);
      await fetchData();
    } catch (err) {
      console.error("Failed to replace user:", err);
      showToast(err.message || "Failed to replace user.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDeleteMember = async () => {
    if (!deleteTargetUser) return;

    try {
      setSubmitting(true);
      const uId = deleteTargetUser.userId || deleteTargetUser.user_id;
      await callApi(instance, accounts, "POST", "/api/departments/delete-member", {
        userId: uId,
      });

      showToast("Member removed from hierarchy successfully!", "success");
      setShowDeleteMemberModal(false);
      setDeleteTargetUser(null);
      setSelectedNode(null);
      await fetchData();
    } catch (err) {
      console.error("Failed to delete member:", err);
      showToast(err.message || "Failed to delete member.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelDeptConflict = () => {
    if (deptConflictData && deptConflictData.previousDeptIds) {
      const restoredIds = deptConflictData.previousDeptIds;
      setAssignDeptIds(restoredIds);

      if (selectedNode) {
        const restoredObjs = restoredIds.map((id) => {
          const foundDept = departments.find((d) => String(d.departmentId || d.department_id) === String(id));
          return {
            departmentId: id,
            department_id: id,
            departmentName: foundDept ? (foundDept.departmentName || foundDept.department_name) : `Dept ${id}`,
            department_name: foundDept ? (foundDept.departmentName || foundDept.department_name) : `Dept ${id}`,
          };
        });
        setSelectedNode((prev) => (prev ? { ...prev, departments: restoredObjs } : null));
      }
    }
    setShowDeptConflictModal(false);
    setShowAssignModal(true);
    setDeptConflictData(null);
    showToast("Department access change cancelled. Department access remains active.", "info");
  };

  const handleResolveDeptConflictSubmit = async () => {
    if (!deptConflictData || !deptConflictData.affectedSubordinates) return;

    try {
      setSubmitting(true);
      for (const item of deptConflictData.affectedSubordinates) {
        const subId = item.subordinate.userId || item.subordinate.user_id;
        const subRoleId = item.subordinate.roleId || item.subordinate.role_id || item.subordinate.currentRoleId || null;
        const deptIdStr = String(item.conflictDept.departmentId || item.conflictDept.department_id || item.conflictDept);

        if (item.selectedAction === "move" && item.selectedTargetManagerId) {
          await callApi(instance, accounts, "POST", "/api/departments/assign-user", {
            userId: subId,
            roleId: subRoleId,
            departmentIds: [deptIdStr],
            reportingToId: item.selectedTargetManagerId,
          });
        } else {
          await callApi(instance, accounts, "POST", "/api/departments/assign-user", {
            userId: subId,
            roleId: subRoleId,
            departmentIds: [],
            reportingToId: null,
          });
        }
      }

      await executeAssignUserSubmit(assignUserId, assignRoleId, assignDeptIds, assignReportingToId);
    } catch (err) {
      showToast(err?.data?.message || err?.message || "Failed to resolve department conflict", "error");
      setSubmitting(false);
    }
  };


  return (
    <div style={s.page}>
      <div style={{ padding: "24px", width: "100%", boxSizing: "border-box" }}>
        {/* HEADER */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
          <div>
            <div style={s.breadcrumb}>
              <span>Administration</span> &gt;{" "}
              <span
                onClick={() => navigate("/manage")}
                style={{ color: "#2563eb", fontWeight: "600", cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                title="Back to Manage"
              >
                Manage
              </span>{" "}
              &gt;{" "}
              {viewMode !== "menu" ? (
                <>
                  <span
                    onClick={() => setViewMode("menu")}
                    style={{ color: "#2563eb", fontWeight: "600", cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                    onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                    title="Back to Departments & Hierarchy"
                  >
                    Departments &amp; Hierarchy
                  </span>{" "}
                  &gt;{" "}
                  <span style={{ color: "#0f172a", fontWeight: "600" }}>
                    {viewMode === "directory"
                      ? "Department Directory"
                      : viewMode === "roles"
                        ? "Dynamic Role Hierarchy Configuration"
                        : viewMode === "role-users"
                          ? "Role-Wise User Directory"
                          : "Reporting Hierarchy Tree"}
                  </span>
                </>
              ) : (
                <span style={{ color: "#0f172a", fontWeight: "600" }}>Departments &amp; Hierarchy</span>
              )}
            </div>
            <h1 style={s.title}>
              {viewMode === "menu"
                ? "Departments & Hierarchy"
                : viewMode === "directory"
                  ? "Department Directory"
                  : viewMode === "roles"
                    ? "Dynamic Role Hierarchy Configuration"
                    : viewMode === "role-users"
                      ? "Role-Wise User Directory"
                      : "Reporting Hierarchy Tree"}
            </h1>
            <p style={s.sub}>
              {viewMode === "menu"
                ? "Manage dynamic departments, custom hierarchy roles, and interactive reporting-to org trees."
                : viewMode === "role-users"
                  ? "View users grouped by dynamic hierarchy roles."
                  : viewMode === "roles"
                    ? "Configure default roles and create custom upside hierarchy levels."
                    : viewMode === "directory"
                      ? "View active departments and assigned members."
                      : "Interactive top-down org tree based on Reporting To relationships."}
            </p>
          </div>

          {(viewMode === "directory" || viewMode === "role-users") && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "12px", position: "relative" }}>
              {/* SLEEK CUSTOM FLOATING DEPARTMENT FILTER DROPDOWN */}
              <div style={{ position: "relative" }}>
                <div
                  onClick={() => setIsDeptFilterDropdownOpen(!isDeptFilterDropdownOpen)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: "#ffffff",
                    padding: "8px 14px",
                    borderRadius: "10px",
                    border: isDeptFilterDropdownOpen ? "2px solid #2563eb" : "1.5px solid #cbd5e1",
                    boxShadow: isDeptFilterDropdownOpen ? "0 0 0 3px rgba(37,99,235,0.15)" : "0 2px 6px rgba(15,23,42,0.03)",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    minWidth: "190px",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Filter size={14} color="#2563eb" />
                    <span style={{ fontSize: "12.5px", fontWeight: "800", color: "#0f172a" }}>
                      {deptFilter === "all"
                        ? `All Departments (${departments.length})`
                        : (() => {
                          const sel = departments.find((d) => String(d.departmentId || d.department_id) === String(deptFilter));
                          return sel ? (sel.departmentName || sel.department_name) : "Select Dept";
                        })()}
                    </span>
                  </div>
                  <ChevronDown
                    size={14}
                    color="#64748b"
                    style={{
                      transform: isDeptFilterDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </div>

                {/* CUSTOM FLOATING POPUP MENU (NO SEARCH INPUT!) */}
                {isDeptFilterDropdownOpen && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      right: 0,
                      background: "#ffffff",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: "12px",
                      boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18)",
                      zIndex: 999999,
                      minWidth: "220px",
                      maxHeight: "260px",
                      overflowY: "auto",
                      padding: "4px 0",
                    }}
                  >
                    {/* OPTION 1: ALL DEPARTMENTS */}
                    <div
                      onClick={() => {
                        setDeptFilter("all");
                        setIsDeptFilterDropdownOpen(false);
                      }}
                      style={{
                        padding: "9px 14px",
                        cursor: "pointer",
                        background: deptFilter === "all" ? "#eff6ff" : "#ffffff",
                        borderBottom: "1px solid #f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: "12.5px",
                        fontWeight: "700",
                        color: deptFilter === "all" ? "#1d4ed8" : "#0f172a",
                      }}
                    >
                      <span>All Departments ({departments.length})</span>
                      {deptFilter === "all" && <Check size={14} color="#2563eb" />}
                    </div>

                    {/* DEPARTMENTS LIST */}
                    {departments.map((d) => {
                      const dId = String(d.departmentId || d.department_id);
                      const dName = d.departmentName || d.department_name;
                      const isSelected = String(deptFilter) === dId;

                      return (
                        <div
                          key={dId}
                          onClick={() => {
                            setDeptFilter(dId);
                            setIsDeptFilterDropdownOpen(false);
                          }}
                          style={{
                            padding: "9px 14px",
                            cursor: "pointer",
                            background: isSelected ? "#eff6ff" : "#ffffff",
                            borderBottom: "1px solid #f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            fontSize: "12.5px",
                            fontWeight: "700",
                            color: isSelected ? "#1d4ed8" : "#334155",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Building2 size={13} color={isSelected ? "#2563eb" : "#64748b"} />
                            <span>{dName}</span>
                          </div>
                          {isSelected && <Check size={14} color="#2563eb" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                style={{ ...s.refreshBtn, display: "inline-flex", alignItems: "center", gap: "6px" }}
                onClick={() => {
                  setFocusedNodeId(null);
                  fetchData();
                }}
                disabled={loading}
              >
                <RefreshCw size={13} style={loading ? { animation: "spin 0.85s linear infinite" } : {}} />
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          )}
        </div>

        {/* TOAST MESSAGE */}
        {toast && (
          <div style={{ ...s.toast, background: toast.type === "error" ? "#fef2f2" : "#f0fdf4", color: toast.type === "error" ? "#991b1b" : "#166534" }}>
            {toast.message}
          </div>
        )}

        {/* INITIAL PAGE LOADING STATE (Exclusively rendered when no data is loaded yet) */}
        {loading && departments.length === 0 && allUsers.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "60px 20px",
              margin: "20px 0",
              background: "#ffffff",
              borderRadius: "16px",
              border: "1.5px solid #e2e8f0",
              boxShadow: "0 10px 25px rgba(15, 23, 42, 0.04)",
              minHeight: "320px",
            }}
          >
            <div
              style={{
                width: "42px",
                height: "42px",
                border: "4px solid #e2e8f0",
                borderTopColor: "#2563eb",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                marginBottom: "14px",
              }}
            />
            <div style={{ fontSize: "14px", fontWeight: "800", color: "#0f172a" }}>
              Loading Department &amp; Hierarchy Data...
            </div>
            <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
              Please wait...
            </div>
          </div>
        ) : (
          <>
            {/* ================= VIEW 1: MENU HUB ================= */}
            {viewMode === "menu" && (
              <div style={s.grid}>
                <div style={{ ...s.menuCard, borderLeft: "6px solid #2563eb" }} onClick={() => setViewMode("directory")}>
                  <div style={s.iconBox}><Building2 size={20} color="#2563eb" /></div>
                  <h3 style={s.cardTitle}>Department Directory</h3>
                  <p style={s.cardDesc}>View active departments and assigned members.</p>
                </div>

                <div style={{ ...s.menuCard, borderLeft: "6px solid #10b981" }} onClick={() => setViewMode("hierarchy")}>
                  <div style={{ ...s.iconBox, background: "rgba(16, 185, 129, 0.1)" }}><Network size={20} color="#10b981" /></div>
                  <h3 style={s.cardTitle}>Reporting Hierarchy Tree</h3>
                  <p style={s.cardDesc}>Interactive top-down org tree based on Reporting To relationships.</p>
                </div>

                <div
                  style={{ ...s.menuCard, borderLeft: "6px solid #ec4899" }}
                  onClick={() => setViewMode("role-users")}
                >
                  <div style={{ ...s.iconBox, background: "rgba(236, 72, 153, 0.1)" }}><UserCheck size={20} color="#ec4899" /></div>
                  <h3 style={s.cardTitle}>Role-Wise User Directory</h3>
                  <p style={s.cardDesc}>View users grouped by dynamic hierarchy roles.</p>
                </div>


                <div style={{ ...s.menuCard, borderLeft: "6px solid #7c3aed" }} onClick={() => setViewMode("roles")}>
                  <div style={{ ...s.iconBox, background: "rgba(124, 58, 237, 0.1)" }}><ShieldCheck size={20} color="#7c3aed" /></div>
                  <h3 style={s.cardTitle}>Dynamic Role Hierarchy Configuration</h3>
                  <p style={s.cardDesc}>Configure default roles (Manager, Team Member) and create custom upside hierarchy levels.</p>
                </div>
              </div>
            )}

            {/* ================= VIEW 2: DEPARTMENT DIRECTORY ================= */}
            {viewMode === "directory" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                {/* DIRECTORY ACTION BAR */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#ffffff", padding: "14px 20px", borderRadius: "14px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(15,23,42,0.03)", flexWrap: "wrap", gap: "12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", background: "#f8fafc", padding: "0 12px", borderRadius: "10px", border: "1px solid #cbd5e1", width: "300px", height: "40px" }}>
                    <Search size={16} color="#94a3b8" />
                    <input
                      placeholder="Search departments..."
                      value={newDeptDesc}
                      onChange={(e) => setNewDeptDesc(e.target.value)}
                      style={{ border: "none", background: "transparent", outline: "none", width: "100%", fontSize: "13px", fontWeight: "600" }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button style={s.refreshBtn} onClick={fetchData} disabled={loading}>{loading ? "Refreshing..." : "Refresh"}</button>
                    <button style={s.primaryBtn} onClick={() => setShowAddDeptModal(true)}>+ Add Department</button>
                  </div>
                </div>

                <div style={s.grid}>
                  {departments.map((dept) => (
                    <div
                      key={dept.departmentId || dept.department_id}
                      style={{
                        ...s.menuCard,
                        borderLeft: "6px solid #2563eb",
                        cursor: "pointer",
                        transition: "all 0.2s ease-in-out",
                      }}
                      onClick={() => handleOpenDeptMembersModal(dept)}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Building2 size={20} color="#2563eb" />
                        <span style={s.activeBadge}><CheckCircle2 size={12} style={{ marginRight: 4 }} /> Active</span>
                      </div>
                      <h3 style={s.cardTitle}>{dept.departmentName || dept.department_name}</h3>
                      <div style={s.deptCodeBadge}>[{dept.departmentCode || dept.department_code}]</div>
                      <p style={s.cardDesc}>{dept.departmentDescription || dept.department_description || "No description."}</p>

                      <div
                        style={{
                          marginTop: "14px",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "6px 12px",
                          background: "#eff6ff",
                          borderRadius: "8px",
                          color: "#1d4ed8",
                          fontSize: "12px",
                          fontWeight: "700",
                          border: "1px solid #bfdbfe",
                          cursor: "pointer",
                        }}
                      >
                        <Users size={14} color="#2563eb" />
                        <span>{dept.memberCount || 0} Members</span>
                        <span style={{ fontSize: "11px", opacity: 0.8, marginLeft: "4px" }}>• Click to View →</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ================= VIEW 3: CIRCULAR HIERARCHY TREE CANVAS ================= */}
            {viewMode === "hierarchy" && (() => {
              // eslint-disable-next-line no-unused-vars
              const findNodeInTree = (nodes, targetId) => {
                if (!Array.isArray(nodes)) return null;
                for (const node of nodes) {
                  if (String(node.userId) === String(targetId)) return node;
                  if (node.subordinates && node.subordinates.length > 0) {
                    const found = findNodeInTree(node.subordinates, targetId);
                    if (found) return found;
                  }
                }
                return null;
              };

              return (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", width: "100%" }}>
                  {/* REPORTING HIERARCHY ACTION BAR */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#ffffff", padding: "8px 16px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(15,23,42,0.03)", width: "100%", boxSizing: "border-box", flexWrap: "wrap", gap: "8px" }}>
                    <div style={{ fontSize: "13.5px", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                      <Users size={16} color="#2563eb" /> Reporting Hierarchy Tree ({allUsers.length} Matched Members)
                    </div>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                      <button style={{ ...s.primaryBtn, padding: "7px 14px", fontSize: "12.5px" }} onClick={() => {
                        setAssignUserId("");
                        setAssignRoleId("");
                        setAssignDeptIds([]);
                        setAssignReportingToId("");
                        setIsAssignAccessOnly(false);
                        setShowAssignModal(true);
                      }}>
                        + Map User Hierarchy
                      </button>
                    </div>
                  </div>

                  {/* USER INTERACTIVE ORG GRAPH CANVAS */}
                  <UserInteractiveGraphCanvas
                    reportingTree={reportingTree}
                    selectedUser={selectedNode}
                    onSelectUser={(u) => setSelectedNode(u)}
                    onExpandDepts={() => setIsDeptsExpanded(true)}
                    onConnectUsers={async (childId, parentId) => {
                      try {
                        const childObj = allUsers.find((u) => String(u.userId || u.user_id) === String(childId));
                        const parentObj = allUsers.find((u) => String(u.userId || u.user_id) === String(parentId));

                        const childRoleId = childObj?.roleId || childObj?.role_id || childObj?.currentRoleId;
                        const parentRoleId = parentObj?.roleId || parentObj?.role_id || parentObj?.currentRoleId;

                        const childRoleObj = hierarchyRoles.find(
                          (r) => String(r.roleId) === String(childRoleId) || (childObj?.currentRoleName && r.roleName === childObj.currentRoleName)
                        );
                        const parentRoleObj = hierarchyRoles.find(
                          (r) => String(r.roleId) === String(parentRoleId) || (parentObj?.currentRoleName && r.roleName === parentObj.currentRoleName)
                        );

                        // Enforce strict role hierarchy order!
                        if (childRoleObj && childRoleObj.parentRoleId) {
                          const allowedParentRoleId = String(childRoleObj.parentRoleId);
                          if (parentRoleObj && String(parentRoleObj.roleId) !== allowedParentRoleId) {
                            const allowedRoleObj = hierarchyRoles.find((r) => String(r.roleId) === allowedParentRoleId);
                            showToast(
                              `Invalid Hierarchy Connection! A ${childRoleObj.roleName || "User"} can only report to a ${allowedRoleObj?.roleName || "superior role"} (cannot report to ${parentRoleObj?.roleName || "this role"}).`,
                              "error"
                            );
                            return;
                          }
                        }

                        let deptIds = [];
                        if (childObj?.departments && Array.isArray(childObj.departments)) {
                          deptIds = childObj.departments.map((d) => d.departmentId || d.department_id || d.id).filter(Boolean);
                        } else if (childObj?.departmentId || childObj?.department_id) {
                          deptIds = [childObj.departmentId || childObj.department_id];
                        }

                        await callApi(instance, accounts, "POST", "/api/departments/assign-user", {
                          userId: childId,
                          roleId: childRoleId,
                          departmentIds: deptIds,
                          reportingToId: parentId,
                        });
                        showToast("User reporting relationship updated successfully!");
                        fetchData();
                      } catch (err) {
                        showToast(err?.data?.message || err?.message || "Failed to update reporting relationship", "error");
                      }
                    }}
                    onDisconnectUser={async (childId) => {
                      try {
                        const childObj = allUsers.find((u) => String(u.userId || u.user_id) === String(childId));
                        const rId = childObj?.roleId || childObj?.role_id || childObj?.currentRoleId || null;
                        const childDeptIds = (childObj?.departments || []).map((d) => String(d.departmentId || d.department_id));

                        // Preserve user's assigned departments when disconnecting from parent manager
                        await callApi(instance, accounts, "POST", "/api/departments/assign-user", {
                          userId: childId,
                          roleId: rId,
                          departmentIds: childDeptIds,
                          reportingToId: null,
                        });
                        const userNameStr = childObj ? `${childObj.firstName || childObj.first_name || ""} ${childObj.lastName || childObj.last_name || ""}`.trim() : "User";
                        showToast(`Set "${userNameStr}" free as independent root! Department access preserved.`);
                        fetchData();
                      } catch (err) {
                        showToast(err?.data?.message || err?.message || "Failed to disconnect reporting relationship", "error");
                      }
                    }}
                  />

                  {/* DOCKED FLOATING EXECUTIVE DETAIL PANEL */}
                  {selectedNode && (
                    <div
                      style={{
                        position: "fixed",
                        top: "50%",
                        right: "24px",
                        transform: "translateY(-50%)",
                        width: "250px",
                        background: "#ffffff",
                        border: `2px solid ${getRoleColor(selectedNode.roleCode).border}`,
                        borderRadius: "14px",
                        padding: "13px",
                        boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18)",
                        zIndex: 999,
                        animation: "childrenCascadeDown 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "800", color: "#0f172a" }}>
                          {selectedNode.firstName} {selectedNode.lastName}
                        </h3>
                        <button
                          style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", padding: "1px" }}
                          onClick={() => setSelectedNode(null)}
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div style={{ fontSize: "11px", color: getRoleColor(selectedNode.roleCode).color, fontWeight: "700", marginTop: "3px" }}>
                        Role: {selectedNode.roleName || "Team Member"}
                      </div>
                      {selectedNode.managerName && (
                        <div style={{ fontSize: "11px", color: "#2563eb", marginTop: "3px", fontWeight: "700" }}>
                          Reporting To: {selectedNode.managerName}
                        </div>
                      )}
                      <div style={{ fontSize: "10.5px", color: "#64748b", marginTop: "3px" }}>
                        Email: {selectedNode.email}
                      </div>
                      {(() => {
                        const depts = selectedNode.departments || [];
                        if (depts.length === 0) {
                          return (
                            <div style={{ fontSize: "10.5px", color: "#94a3b8", marginTop: "4px", fontWeight: "600" }}>
                              Assigned Depts: None
                            </div>
                          );
                        }
                        if (depts.length === 1) {
                          return (
                            <div style={{ fontSize: "10.5px", color: "#475569", marginTop: "4px", fontWeight: "600" }}>
                              Assigned Depts: <span style={{ color: "#2563eb", fontWeight: "700" }}>{depts[0].departmentName}</span>
                            </div>
                          );
                        }

                        const firstDept = depts[0].departmentName;
                        const extraCount = depts.length - 1;

                        if (isDeptsExpanded) {
                          return (
                            <div style={{ fontSize: "10.5px", color: "#475569", marginTop: "4px", fontWeight: "600", display: "flex", flexDirection: "column", gap: "4px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <span>Assigned Depts ({depts.length}):</span>
                                <span
                                  onClick={() => setIsDeptsExpanded(false)}
                                  style={{ color: "#2563eb", fontSize: "10px", fontWeight: "800", cursor: "pointer", textDecoration: "underline" }}
                                >
                                  Show Less ▲
                                </span>
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "2px" }}>
                                {depts.map((d) => (
                                  <span
                                    key={d.departmentId || d.department_id}
                                    style={{
                                      background: "#eff6ff",
                                      color: "#1d4ed8",
                                      padding: "2px 7px",
                                      borderRadius: "6px",
                                      fontSize: "10px",
                                      fontWeight: "700",
                                      border: "1px solid #bfdbfe",
                                    }}
                                  >
                                    🏢 {d.departmentName || d.department_name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div style={{ fontSize: "10.5px", color: "#475569", marginTop: "4px", fontWeight: "600", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px" }}>
                            <span>Assigned Depts:</span>
                            <span style={{ color: "#2563eb", fontWeight: "700" }}>{firstDept}</span>
                            <span
                              onClick={() => setIsDeptsExpanded(true)}
                              title="Click to view all assigned departments"
                              style={{
                                background: "#eff6ff",
                                color: "#2563eb",
                                padding: "1px 7px",
                                borderRadius: "10px",
                                fontSize: "10px",
                                fontWeight: "800",
                                border: "1px solid #bfdbfe",
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                                boxShadow: "0 1px 3px rgba(37,99,235,0.1)",
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.background = "#dbeafe"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = "#eff6ff"; }}
                            >
                              +{extraCount} more ▼
                            </span>
                          </div>
                        );
                      })()}
                      {(() => {
                        const roleCode = String(selectedNode.roleCode || selectedNode.role_code || selectedNode.currentRoleCode || "").toUpperCase();
                        const roleName = String(selectedNode.roleName || selectedNode.role_name || selectedNode.currentRoleName || "").toUpperCase();
                        // const hasSubordinates = Boolean(
                        //   (selectedNode.subordinates && selectedNode.subordinates.length > 0) ||
                        //   (selectedNode.children && selectedNode.children.length > 0)
                        // );
                        // !hasSubordinates 
                        const isTeamMember = roleCode === "TEAM_MEMBER" || roleName.includes("TEAM MEMBER");

                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "12px" }}>
                            <div style={{ display: "flex", gap: "6px" }}>
                              <button
                                style={{
                                  flex: 1,
                                  padding: "6px 8px",
                                  background: THEME,
                                  color: "#ffffff",
                                  border: "none",
                                  borderRadius: "6px",
                                  fontSize: "11px",
                                  fontWeight: "700",
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  setAssignUserId(String(selectedNode.userId || selectedNode.user_id));
                                  setAssignRoleId(String(selectedNode.roleId || selectedNode.role_id || selectedNode.currentRoleId || ""));
                                  setAssignDeptIds((selectedNode.departments || []).map((d) => String(d.departmentId || d.department_id)));
                                  setAssignReportingToId(selectedNode.reportingToId ? String(selectedNode.reportingToId) : "");
                                  setIsAssignAccessOnly(true);
                                  setShowAssignModal(true);
                                }}
                              >
                                Assign Access
                              </button>
                              {!isTeamMember && (
                                <button
                                  style={{
                                    flex: 1,
                                    padding: "6px 8px",
                                    background: "#10b981",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "700",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    setAssignUserId("");
                                    setAssignRoleId("");
                                    setAssignDeptIds([]);
                                    setAssignReportingToId(String(selectedNode.userId));
                                    setIsAssignAccessOnly(false);
                                    setShowAssignModal(true);
                                  }}
                                >
                                  + Add Member
                                </button>
                              )}
                            </div>

                            {/* LAST FULL ROW BELOW ASSIGN ACCESS AND + ADD MEMBER */}
                            {!isTeamMember ? (
                              <button
                                style={{
                                  width: "100%",
                                  padding: "7px 10px",
                                  background: "#64748b",
                                  color: "#ffffff",
                                  border: "none",
                                  borderRadius: "6px",
                                  fontSize: "11.5px",
                                  fontWeight: "700",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                  boxShadow: "0 2px 6px rgba(100, 116, 139, 0.2)",
                                }}
                                onClick={() => {
                                  setReplaceOldUser(selectedNode);
                                  setReplaceNewUserId("");
                                  setShowReplaceModal(true);
                                }}
                              >
                                <RefreshCw size={13} /> Replace User
                              </button>
                            ) : (
                              <button
                                style={{
                                  width: "100%",
                                  padding: "7px 10px",
                                  background: "#dc2626",
                                  color: "#ffffff",
                                  border: "none",
                                  borderRadius: "6px",
                                  fontSize: "11.5px",
                                  fontWeight: "700",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                  boxShadow: "0 2px 6px rgba(220, 38, 38, 0.2)",
                                }}
                                onClick={() => {
                                  setDeleteTargetUser(selectedNode);
                                  setShowDeleteMemberModal(true);
                                }}
                              >
                                <Trash2 size={13} /> Delete Member
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* ================= VIEW 4: DYNAMIC ROLE HIERARCHY TREE PAGE ================= */}
            {viewMode === "roles" && (() => {
              // Build role reporting tree hierarchy based on parentRoleId
              const roleMap = new Map();
              hierarchyRoles.forEach((r) => {
                roleMap.set(r.roleId, { ...r, subRoles: [] });
              });

              const rootRoles = [];
              roleMap.forEach((roleObj) => {
                if (roleObj.parentRoleId && roleMap.has(roleObj.parentRoleId)) {
                  roleMap.get(roleObj.parentRoleId).subRoles.push(roleObj);
                } else {
                  rootRoles.push(roleObj);
                }
              });

              // Recursive Role Tree Node Pod Component with Cycle Guard
              const RecursiveRolePod = ({ roleNode, visited = new Set() }) => {
                if (!roleNode || visited.has(roleNode.roleId)) return null;
                const currentVisited = new Set(visited).add(roleNode.roleId);

                const isSelectedRole = selectedRole && selectedRole.roleId === roleNode.roleId;
                const isDragOver = dragOverRoleId && String(dragOverRoleId) === String(roleNode.roleId);
                const roleInitials = getInitials(roleNode.roleName);
                const hasChildren = roleNode.subRoles && roleNode.subRoles.length > 0;

                return (
                  <div
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}
                    onMouseUp={() => {
                      if (connectingSourceRoleId && String(connectingSourceRoleId) !== String(roleNode.roleId)) {
                        handleDropRoleConnect(connectingSourceRoleId, roleNode.roleId);
                        setConnectingSourceRoleId(null);
                        setDragOverRoleId(null);
                      }
                    }}
                    onMouseEnter={() => {
                      if (connectingSourceRoleId && String(connectingSourceRoleId) !== String(roleNode.roleId)) {
                        setDragOverRoleId(roleNode.roleId);
                      }
                    }}
                    onMouseLeave={() => {
                      if (String(dragOverRoleId) === String(roleNode.roleId)) setDragOverRoleId(null);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "link";
                      if (e.clientX || e.clientY) {
                        setDragCurrentPos({ x: e.clientX, y: e.clientY });
                      }
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      if (draggingRoleId && String(draggingRoleId) !== String(roleNode.roleId)) {
                        setDragOverRoleId(roleNode.roleId);
                      }
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      if (String(dragOverRoleId) === String(roleNode.roleId)) setDragOverRoleId(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const draggedId = e.dataTransfer.getData("text/plain") || draggingRoleId;
                      if (draggedId && String(draggedId) !== String(roleNode.roleId)) {
                        handleDropRoleConnect(draggedId, roleNode.roleId);
                      }
                    }}
                  >
                    {/* CIRCLE ROLE POD WITH DRAG & DROP CONNECTIVITY */}
                    <div
                      draggable={true}
                      onDragStart={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const centerX = rect.left + rect.width / 2;
                        const centerY = rect.top + rect.height / 2;
                        setDragStartPos({ x: centerX, y: centerY });
                        setDragCurrentPos({ x: e.clientX || centerX, y: e.clientY || centerY });
                        e.dataTransfer.setData("text/plain", String(roleNode.roleId));
                        setDraggingRoleId(roleNode.roleId);
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "link";
                      }}
                      onDragEnd={() => {
                        setDraggingRoleId(null);
                        setDragOverRoleId(null);
                      }}
                      onClick={() => setSelectedRole(isSelectedRole ? null : roleNode)}
                      style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "50%",
                        background: isDragOver ? "#eff6ff" : "#ffffff",
                        border: isDragOver
                          ? `2px dashed #2563eb`
                          : isSelectedRole
                            ? `2.5px solid #2563eb`
                            : `1.5px solid #2563eb`,
                        boxShadow: isDragOver
                          ? "0 0 0 3px rgba(37, 99, 235, 0.3), 0 4px 12px rgba(37, 99, 235, 0.2)"
                          : isSelectedRole
                            ? `0 0 0 3px #eff6ff, 0 4px 12px rgba(37, 99, 235, 0.15)`
                            : "0 2px 6px rgba(15, 23, 42, 0.05)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "grab",
                        position: "relative",
                        opacity: 1,
                        transition: "transform 0.25s ease, boxShadow 0.2s ease",
                        transform: isDragOver ? "scale(1.1)" : isSelectedRole ? "scale(1.05)" : "scale(1)",
                      }}
                    >
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "50%",
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          fontSize: "13.5px",
                          fontWeight: "800",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {roleInitials}
                      </div>

                      {/* INTERACTIVE CONNECTOR PORT HANDLE (+ BADGE) */}
                      <div
                        title="Click & Drag line to connect to a manager/parent role"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          const handleEl = e.currentTarget;
                          const canvasEl = handleEl.closest(".tree-canvas-container");
                          if (canvasEl) {
                            const canvasRect = canvasEl.getBoundingClientRect();
                            const handleRect = handleEl.getBoundingClientRect();
                            setDragStartPos({
                              x: handleRect.left + handleRect.width / 2 - canvasRect.left,
                              y: handleRect.top + handleRect.height / 2 - canvasRect.top,
                            });
                            setDragCurrentPos({
                              x: e.clientX - canvasRect.left,
                              y: e.clientY - canvasRect.top,
                            });
                          }
                          setConnectingSourceRoleId(roleNode.roleId);
                        }}
                        style={{
                          position: "absolute",
                          bottom: "-5px",
                          right: "-5px",
                          width: "18px",
                          height: "18px",
                          borderRadius: "50%",
                          background: "#7c3aed",
                          color: "#ffffff",
                          fontSize: "11px",
                          fontWeight: "900",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "crosshair",
                          zIndex: 10,
                          border: "2px solid #ffffff",
                          boxShadow: "0 2px 6px rgba(124, 58, 237, 0.35)",
                        }}
                      >
                        +
                      </div>
                    </div>

                    <div style={{ textAlign: "center", marginTop: "8px" }}>
                      <div style={{ fontSize: "12.5px", fontWeight: "800", color: "#0f172a", whiteSpace: "nowrap" }}>
                        {roleNode.roleName}
                      </div>
                      {roleNode.parentRoleName && (
                        <div style={{ fontSize: "10.5px", color: "#2563eb", fontWeight: "700", marginTop: "2px", display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                          <span>Reports to {roleNode.parentRoleName}</span>
                          <button
                            title={`Disconnect ${roleNode.roleName} from ${roleNode.parentRoleName}`}
                            style={{
                              background: "#fef2f2",
                              color: "#dc2626",
                              border: "1px solid #fecaca",
                              borderRadius: "50%",
                              width: "14px",
                              height: "14px",
                              fontSize: "10px",
                              fontWeight: "900",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 0,
                              lineHeight: 1,
                            }}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await callApi(instance, accounts, "PUT", `/api/departments/roles/${roleNode.roleId}/parent`, {
                                  parentRoleId: null,
                                });
                                showToast(`Disconnected "${roleNode.roleName}" reporting line!`);
                                fetchData();
                              } catch (err) {
                                showToast(err?.data?.message || err.message || "Failed to disconnect role", "error");
                              }
                            }}
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>

                    {/* SUBORDINATE CHILD ROLES BRANCH */}
                    {hasChildren && (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "10px", width: "100%" }}>
                        <CurvedBranchCanvas childCount={roleNode.subRoles.length} color="#94a3b8" />
                        <div style={{ display: "flex", gap: "48px", justifyContent: "center", alignItems: "flex-start", position: "relative" }}>
                          {roleNode.subRoles.map((subRole) => (
                            <div key={subRole.roleId} style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100px", flexShrink: 0 }}>
                              <RecursiveRolePod roleNode={subRole} visited={currentVisited} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", width: "100%" }}>
                  {/* ROLE MANAGEMENT ACTION BAR */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#ffffff", padding: "8px 16px", borderRadius: "12px", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(15,23,42,0.03)", width: "100%", boxSizing: "border-box", flexWrap: "wrap", gap: "8px" }}>
                    <div style={{ fontSize: "13.5px", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                      <ShieldCheck size={16} color="#2563eb" /> Dynamic Role Hierarchy Tree ({hierarchyRoles.length} Roles)
                    </div>
                    <button
                      style={{ ...s.primaryBtn, background: "#2563eb", padding: "7px 14px", fontSize: "12.5px" }}
                      onClick={() => {
                        setSelectedRole(null);
                        setNewRoleName("");
                        setNewRoleCode("");
                        const existingTopRoot = hierarchyRoles.find((r) => !r.parentRoleId);
                        setNewRoleParentId(existingTopRoot ? String(existingTopRoot.roleId) : "");
                        setNewRoleMultiDept(true);
                        setShowAddRoleModal(true);
                      }}
                    >
                      + Add Dynamic Hierarchy Role
                    </button>
                  </div>

                  {/* INTERACTIVE GRAPH CANVAS (DRAG NODES & DRAG LINES TO CONNECT) */}
                  <RoleInteractiveGraphCanvas
                    hierarchyRoles={hierarchyRoles}
                    selectedRole={selectedRole}
                    onSelectRole={(r) => setSelectedRole(selectedRole && selectedRole.roleId === r.roleId ? null : r)}
                    onConnectRoles={(childId, parentId) => handleDropRoleConnect(childId, parentId)}
                    onDeleteRole={(role) => setRoleToDelete(role)}
                    onDisconnectRole={async (roleId) => {
                      try {
                        await callApi(instance, accounts, "PUT", `/api/departments/roles/${roleId}/parent`, {
                          parentRoleId: null,
                        });
                        showToast("Disconnected reporting line!");
                        fetchData();
                      } catch (err) {
                        showToast(err?.data?.message || err.message || "Failed to disconnect role", "error");
                      }
                    }}
                  />
                  {selectedRole && (
                    <div
                      style={{
                        position: "fixed",
                        top: "50%",
                        right: "24px",
                        transform: "translateY(-50%)",
                        width: "260px",
                        background: "#ffffff",
                        border: "2px solid #7c3aed",
                        borderRadius: "14px",
                        padding: "14px",
                        boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18)",
                        zIndex: 99999,
                        animation: "childrenCascadeDown 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "800", color: "#0f172a" }}>
                          {selectedRole.roleName}
                        </h3>
                        <button
                          style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", padding: "1px" }}
                          onClick={() => setSelectedRole(null)}
                        >
                          <X size={16} />
                        </button>
                      </div>
                      {selectedRole.parentRoleName && (
                        <div style={{ fontSize: "11.5px", color: "#2563eb", fontWeight: "700", marginTop: "3px" }}>
                          Reports To Role: {selectedRole.parentRoleName}
                        </div>
                      )}
                      <div style={{ fontSize: "11px", color: "#64748b", marginTop: "3px" }}>
                        Role Code: {selectedRole.roleCode}
                      </div>
                      <div style={{ fontSize: "11px", color: selectedRole.allowMultiDept ? "#1d4ed8" : "#475569", fontWeight: "700", marginTop: "5px" }}>
                        Department Rule: {selectedRole.allowMultiDept ? "Multi-Department Allowed" : "1 Department Strictly"}
                      </div>

                      <div style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
                        <button
                          style={{
                            width: "100%",
                            padding: "7px 10px",
                            background: "#7c3aed",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "11.5px",
                            fontWeight: "700",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setEditingRoleId(selectedRole.roleId);
                            setNewRoleName(selectedRole.roleName);
                            setNewRoleCode(selectedRole.roleCode || "");
                            setNewRoleParentId(selectedRole.parentRoleId ? String(selectedRole.parentRoleId) : "");
                            setNewRoleMultiDept(!!selectedRole.allowMultiDept);
                            setShowAddRoleModal(true);
                          }}
                        >
                          ✏️ Edit Role ({selectedRole.roleName})
                        </button>


                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ================= VIEW 5: ROLE-WISE USER MANAGEMENT PAGE (MASTER-DETAIL LAYOUT) ================= */}
            {viewMode === "role-users" && (() => {
              const currentTabId = selectedRoleTab ? selectedRoleTab.roleId : (hierarchyRoles[0] ? hierarchyRoles[0].roleId : null);
              const activeRole = hierarchyRoles.find((r) => String(r.roleId) === String(currentTabId)) || selectedRoleTab || hierarchyRoles[0];

              const getUsersForRole = (r) => {
                if (!r) return [];
                let rawUsers = [];
                if (Array.isArray(r.roleUsers) && r.roleUsers.length > 0) {
                  rawUsers = r.roleUsers;
                } else {
                  const rId = String(r.roleId);
                  const rCode = (r.roleCode || "").toUpperCase();
                  const rName = (r.roleName || "").toLowerCase();

                  rawUsers = allUsers.filter((u) => {
                    const uRoleId = String(u.roleId || u.role_id || "");
                    const uRoleCode = (u.roleCode || u.role_code || u.currentRoleCode || "").toUpperCase();
                    const uRoleName = (u.roleName || u.role_name || u.currentRoleName || "").toLowerCase();

                    return (
                      (rId && uRoleId === rId) ||
                      (rCode && uRoleCode === rCode) ||
                      (rName && uRoleName === rName) ||
                      ((r.users || []).some((m) => String(m.userId || m.user_id) === String(u.userId || u.user_id)))
                    );
                  });
                }

                if (deptFilter && deptFilter !== "all") {
                  const selectedDeptObj = departments.find((d) => String(d.departmentId || d.department_id) === String(deptFilter));
                  const targetDeptName = selectedDeptObj ? (selectedDeptObj.departmentName || selectedDeptObj.department_name).toLowerCase() : "";

                  return rawUsers.filter((u) => {
                    const uDepts = Array.isArray(u.departments) && u.departments.length > 0
                      ? u.departments
                      : u.departmentName
                        ? [{ departmentName: u.departmentName }]
                        : [];

                    return uDepts.some((d) => {
                      const dId = String(d.departmentId || d.department_id || "");
                      const dName = (d.departmentName || d.department_name || d || "").toLowerCase();
                      return (deptFilter && dId === String(deptFilter)) || (targetDeptName && dName === targetDeptName);
                    });
                  });
                }

                return rawUsers;
              };

              const activeRoleUsers = getUsersForRole(activeRole);

              return (
                <div style={{ display: "flex", gap: "20px", width: "100%", alignItems: "flex-start", marginTop: "4px" }}>
                  {/* LEFT COLUMN: MASTER ROLE LIST */}
                  <div style={{ width: "320px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "10px", background: "#ffffff", padding: "16px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(15,23,42,0.03)" }}>
                    <div style={{ fontSize: "13px", fontWeight: "800", color: "#475569", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Select Role ({hierarchyRoles.length})
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "600px", overflowY: "auto", paddingRight: "2px" }}>
                      {hierarchyRoles.map((role) => {
                        const isSelected = activeRole && String(activeRole.roleId) === String(role.roleId);
                        const count = getUsersForRole(role).length;

                        return (
                          <div
                            key={role.roleId}
                            onClick={() => setSelectedRoleTab(role)}
                            style={{
                              padding: "12px 14px",
                              borderRadius: "12px",
                              cursor: "pointer",
                              background: isSelected ? "#eff6ff" : "#f8fafc",
                              border: `1.5px solid ${isSelected ? "#2563eb" : "#e2e8f0"}`,
                              boxShadow: isSelected ? "0 4px 12px rgba(37,99,235,0.1)" : "none",
                              transition: "all 0.15s ease",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <div style={{ fontSize: "13.5px", fontWeight: "800", color: isSelected ? "#1d4ed8" : "#0f172a" }}>
                                {role.roleName}
                              </div>
                              <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600", marginTop: "2px" }}>
                                Code: <span style={{ color: "#2563eb" }}>[{role.roleCode}]</span>
                              </div>
                            </div>

                            <span style={{ background: isSelected ? "#2563eb" : "#cbd5e1", color: "#ffffff", fontSize: "11px", fontWeight: "800", padding: "2px 8px", borderRadius: "12px" }}>
                              {count}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* RIGHT COLUMN: DETAIL PANEL FOR SELECTED ROLE */}
                  {activeRole ? (
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "16px", background: "#ffffff", padding: "20px", borderRadius: "16px", border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(15,23,42,0.03)", minHeight: "450px" }}>
                      {/* ROLE TITLE */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "14px", borderBottom: "1px solid #e2e8f0" }}>
                        <div>
                          <div style={{ fontSize: "18px", fontWeight: "800", color: "#0f172a" }}>
                            {activeRole.roleName}
                          </div>
                          <div style={{ fontSize: "12px", color: "#64748b", fontWeight: "600", marginTop: "4px" }}>
                            Assigned Users: <strong style={{ color: "#0f172a" }}>{activeRoleUsers.length} members</strong> • Code: [{activeRole.roleCode}]
                          </div>
                        </div>
                      </div>

                      {/* USERS LIST FOR ACTIVE ROLE */}
                      {activeRoleUsers.length === 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", gap: "8px", textAlign: "center" }}>
                          <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <UserCheck size={24} />
                          </div>
                          <div style={{ fontSize: "15px", fontWeight: "800", color: "#0f172a" }}>No Users Currently Assigned</div>
                          <div style={{ fontSize: "13px", color: "#64748b", maxWidth: "340px" }}>
                            There are currently no users assigned to the <strong>{activeRole.roleName}</strong> role.
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "14px" }}>
                          {activeRoleUsers.map((user) => {
                            const uId = user.userId || user.user_id;
                            const uName = `${user.firstName || user.first_name || ""} ${user.lastName || user.last_name || ""}`.trim() || "User";
                            const uEmail = user.email || user.email_address || "";
                            const initials = getInitials(uName);

                            const isOpen = String(openUserDeptDropdownId) === String(uId);

                            return (
                              <div
                                key={uId}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  background: "#f8fafc",
                                  padding: "14px",
                                  borderRadius: "12px",
                                  border: "1px solid #e2e8f0",
                                  boxShadow: "0 2px 6px rgba(15,23,42,0.02)",
                                  position: "relative",
                                  zIndex: isOpen ? 99999 : 1,
                                }}
                              >
                                <div
                                  style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    background: "#eff6ff",
                                    color: "#2563eb",
                                    fontSize: "14px",
                                    fontWeight: "800",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1.5px solid #bfdbfe",
                                    flexShrink: 0,
                                  }}
                                >
                                  {initials}
                                </div>

                                <div style={{ overflow: "visible" }}>
                                  <div style={{ fontSize: "13.5px", fontWeight: "800", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {uName}
                                  </div>
                                  <div style={{ fontSize: "11px", color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {uEmail}
                                  </div>
                                  {(() => {
                                    const depts = Array.isArray(user.departments) && user.departments.length > 0
                                      ? user.departments
                                      : user.departmentName
                                        ? [{ departmentName: user.departmentName }]
                                        : [];

                                    if (depts.length === 0) return null;

                                    if (depts.length === 1) {
                                      const dName = depts[0].departmentName || depts[0].department_name || depts[0];
                                      return (
                                        <span style={{ display: "inline-block", marginTop: "4px", fontSize: "10px", fontWeight: "700", background: "#f1f5f9", color: "#475569", padding: "2px 6px", borderRadius: "4px", border: "1px solid #e2e8f0" }}>
                                          {dName}
                                        </span>
                                      );
                                    }

                                    const firstName = depts[0].departmentName || depts[0].department_name || depts[0];
                                    const extraCount = depts.length - 1;
                                    const isOpen = String(openUserDeptDropdownId) === String(uId);

                                    return (
                                      <div style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "4px", flexWrap: "wrap" }}>
                                        <span style={{ fontSize: "10px", fontWeight: "700", background: "#f1f5f9", color: "#334155", padding: "2px 6px", borderRadius: "4px", border: "1px solid #cbd5e1" }}>
                                          {firstName}
                                        </span>

                                        {/* CLICKABLE INTERACTIVE DROPDOWN BUTTON FOR MORE DEPARTMENTS */}
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setOpenUserDeptDropdownId(isOpen ? null : uId);
                                          }}
                                          style={{
                                            background: isOpen ? "#1d4ed8" : "#2563eb",
                                            color: "#ffffff",
                                            border: "none",
                                            borderRadius: "6px",
                                            fontSize: "10.5px",
                                            fontWeight: "800",
                                            padding: "2px 8px",
                                            cursor: "pointer",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            boxShadow: "0 2px 6px rgba(37, 99, 235, 0.25)",
                                            transition: "all 0.15s ease",
                                          }}
                                        >
                                          <span>+{extraCount} more</span>
                                          <ChevronDown size={12} style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }} />
                                        </button>

                                        {/* FLOATING DROPDOWN POPUP MENU LISTING ALL REMAINING DEPARTMENTS */}
                                        {isOpen && (
                                          <div
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                              position: "absolute",
                                              top: "calc(100% + 4px)",
                                              left: 0,
                                              background: "#ffffff",
                                              border: "1.5px solid #bfdbfe",
                                              borderRadius: "10px",
                                              boxShadow: "0 12px 30px rgba(15, 23, 42, 0.2)",
                                              zIndex: 99999,
                                              minWidth: "220px",
                                              padding: "8px",
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "6px",
                                            }}
                                          >
                                            <div style={{ fontSize: "10.5px", fontWeight: "800", color: "#1e40af", paddingBottom: "4px", borderBottom: "1px solid #eff6ff", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                              Assigned Departments ({depts.length})
                                            </div>
                                            <div style={{ maxHeight: "180px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "4px" }}>
                                              {depts.map((d, dIdx) => {
                                                const name = d.departmentName || d.department_name || d;
                                                const code = d.departmentCode || d.department_code;
                                                return (
                                                  <div
                                                    key={dIdx}
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "6px",
                                                      fontSize: "11.5px",
                                                      fontWeight: "700",
                                                      color: dIdx === 0 ? "#1e40af" : "#334155",
                                                      background: dIdx === 0 ? "#eff6ff" : "#f8fafc",
                                                      padding: "5px 8px",
                                                      borderRadius: "6px",
                                                      border: `1px solid ${dIdx === 0 ? "#bfdbfe" : "#e2e8f0"}`,
                                                    }}
                                                  >
                                                    <Building2 size={12} color={dIdx === 0 ? "#2563eb" : "#64748b"} />
                                                    <span style={{ flex: 1 }}>{name}</span>
                                                    {code && <span style={{ fontSize: "10px", color: "#64748b", fontWeight: "600" }}>[{code}]</span>}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ flex: 1, padding: "40px", textAlign: "center", color: "#64748b", fontSize: "14px" }}>
                      Select a role from the left list to view assigned users.
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* ================= MODAL: REPLACE USER IN HIERARCHY MODAL ================= */}
      {showReplaceModal && replaceOldUser && (
        <div style={s.modalOverlay} onClick={() => setShowReplaceModal(false)}>
          <div style={{ ...s.modalBox, width: "540px", minHeight: "380px", overflow: "visible" }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, fontSize: "16px" }}>
                Replace User: <span style={{ color: "#2563eb" }}>{replaceOldUser.firstName || replaceOldUser.first_name} {replaceOldUser.lastName || replaceOldUser.last_name}</span>
              </h3>
              <button style={s.closeBtn} onClick={() => setShowReplaceModal(false)}><X size={18} /></button>
            </div>

            <div style={{ ...s.modalBody, gap: "16px", overflow: "visible", minHeight: "260px", paddingBottom: "120px" }}>
              <div style={{ background: "#f8fafc", padding: "12px 14px", borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: "12.5px", color: "#475569" }}>
                Select a new user to replace <strong>{replaceOldUser.firstName || replaceOldUser.first_name} {replaceOldUser.lastName || replaceOldUser.last_name}</strong>. All reporting subordinates and active department assignments will be transferred to the selected user automatically.
              </div>

              <div style={{ ...s.formGroup, position: "relative" }}>
                <label style={s.label}>Select Replacement User *</label>
                {(() => {
                  const filteredCandidates = allUsers.filter((u) => {
                    const uIdStr = String(u.userId || u.user_id);
                    const oldIdStr = String(replaceOldUser.userId || replaceOldUser.user_id);
                    if (uIdStr === oldIdStr) return false;
                    if (!replaceUserSearchQuery.trim()) return true;
                    const q = replaceUserSearchQuery.toLowerCase();
                    const name = `${u.first_name || u.firstName || ""} ${u.last_name || u.lastName || ""}`.toLowerCase();
                    const email = (u.email_address || u.email || "").toLowerCase();
                    return name.includes(q) || email.includes(q);
                  });

                  const selectedReplacementObj = allUsers.find((u) => String(u.userId || u.user_id) === String(replaceNewUserId));

                  return (
                    <div style={{ position: "relative", width: "100%" }}>
                      <div
                        onClick={() => setIsReplaceUserDropdownOpen(!isReplaceUserDropdownOpen)}
                        style={{
                          width: "100%",
                          minHeight: "44px",
                          padding: "8px 14px",
                          background: "#ffffff",
                          border: isReplaceUserDropdownOpen ? "2px solid #2563eb" : "1.5px solid #cbd5e1",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          boxShadow: isReplaceUserDropdownOpen ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
                          boxSizing: "border-box",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <User size={16} color="#2563eb" />
                          <span style={{ fontSize: "13.5px", fontWeight: "700", color: selectedReplacementObj ? "#0f172a" : "#64748b" }}>
                            {selectedReplacementObj
                              ? `${selectedReplacementObj.first_name || selectedReplacementObj.firstName || ""} ${selectedReplacementObj.last_name || selectedReplacementObj.lastName || ""}`.trim() + ` (${selectedReplacementObj.email_address || selectedReplacementObj.email || ""})`
                              : "-- Choose Replacement User --"}
                          </span>
                        </div>
                        <ChevronDown size={16} color="#64748b" style={{ transform: isReplaceUserDropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }} />
                      </div>

                      {isReplaceUserDropdownOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 6px)",
                            left: 0,
                            right: 0,
                            background: "#ffffff",
                            border: "1.5px solid #e2e8f0",
                            borderRadius: "12px",
                            boxShadow: "0 18px 42px rgba(15, 23, 42, 0.22)",
                            zIndex: 999999,
                            maxHeight: "260px",
                            overflowY: "auto",
                          }}
                        >
                          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
                            <input
                              placeholder="Type to search user name or email..."
                              value={replaceUserSearchQuery}
                              onChange={(e) => setReplaceUserSearchQuery(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: "100%", padding: "6px 10px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "12px", outline: "none", boxSizing: "border-box" }}
                            />
                          </div>
                          {filteredCandidates.map((u) => {
                            const uIdStr = String(u.userId || u.user_id);
                            const uName = `${u.firstName || u.first_name || ""} ${u.lastName || u.last_name || ""}`.trim();
                            const isSelected = String(replaceNewUserId) === uIdStr;
                            return (
                              <div
                                key={uIdStr}
                                onClick={() => {
                                  setReplaceNewUserId(uIdStr);
                                  setIsReplaceUserDropdownOpen(false);
                                }}
                                style={{
                                  padding: "10px 14px",
                                  cursor: "pointer",
                                  background: isSelected ? "#eff6ff" : "#ffffff",
                                  borderBottom: "1px solid #f8fafc",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div>
                                  <div style={{ fontSize: "13px", fontWeight: "800", color: isSelected ? "#1d4ed8" : "#0f172a" }}>{uName}</div>
                                  <div style={{ fontSize: "11px", color: "#64748b" }}>{u.email || u.email_address}</div>
                                </div>
                                {isSelected && <Check size={14} color="#2563eb" />}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div style={s.modalFooter}>
              <button type="button" style={s.cancelBtn} onClick={() => setShowReplaceModal(false)}>Cancel</button>
              <button type="button" style={{ ...s.saveBtn, background: "#64748b" }} disabled={submitting || !replaceNewUserId} onClick={handleConfirmReplaceUser}>
                {submitting ? "Replacing..." : "Confirm & Replace User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL: DELETE TEAM MEMBER MODAL ================= */}
      {showDeleteMemberModal && deleteTargetUser && (
        <div style={s.modalOverlay} onClick={() => setShowDeleteMemberModal(false)}>
          <div style={{ ...s.modalBox, width: "460px" }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, fontSize: "16px", color: "#dc2626" }}>
                Delete Team Member
              </h3>
              <button style={s.closeBtn} onClick={() => setShowDeleteMemberModal(false)}><X size={18} /></button>
            </div>

            <div style={{ ...s.modalBody, gap: "14px" }}>
              <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", padding: "14px", borderRadius: "12px", display: "flex", gap: "10px", alignItems: "flex-start" }}>
                <AlertCircle size={20} color="#dc2626" style={{ flexShrink: 0, marginTop: "2px" }} />
                <div style={{ fontSize: "13px", color: "#991b1b", lineHeight: "1.4" }}>
                  Are you sure you want to remove <strong>{deleteTargetUser.firstName || deleteTargetUser.first_name} {deleteTargetUser.lastName || deleteTargetUser.last_name}</strong> from the hierarchy?
                  <br />
                  <span style={{ fontSize: "11.5px", color: "#7f1d1d", marginTop: "4px", display: "inline-block" }}>
                    This will remove their active role and department assignments from the tree.
                  </span>
                </div>
              </div>
            </div>

            <div style={s.modalFooter}>
              <button type="button" style={s.cancelBtn} onClick={() => setShowDeleteMemberModal(false)}>Cancel</button>
              <button type="button" style={s.dangerBtn} disabled={submitting} onClick={handleConfirmDeleteMember}>
                {submitting ? "Removing..." : "Delete Member"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL 0: ROLE USER ASSIGNMENT MODAL WITH CONFLICT WARNING ================= */}
      {showRoleUserAssignModal && targetAssignRole && (
        <div style={s.modalOverlay} onClick={() => setShowRoleUserAssignModal(false)}>
          <div style={{ ...s.modalBox, width: "560px", minHeight: "420px", overflow: "visible" }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, fontSize: "16px" }}>
                Assign User to Role: <span style={{ color: "#2563eb" }}>{targetAssignRole.roleName}</span>
              </h3>
              <button style={s.closeBtn} onClick={() => setShowRoleUserAssignModal(false)}><X size={18} /></button>
            </div>

            <div style={{ ...s.modalBody, gap: "16px", overflow: "visible", minHeight: "340px", paddingBottom: "120px" }}>
              <div style={{ ...s.formGroup, position: "relative" }}>
                <label style={s.label}>Select User to Assign *</label>
                {(() => {
                  const filteredAssignableUsers = assignableUsers.filter((u) => {
                    if (!roleUserSearchQuery.trim()) return true;
                    const q = roleUserSearchQuery.toLowerCase();
                    const name = `${u.first_name || u.firstName || ""} ${u.last_name || u.lastName || ""}`.toLowerCase();
                    const email = (u.email_address || u.email || "").toLowerCase();
                    return name.includes(q) || email.includes(q);
                  });

                  return (
                    <div style={{ position: "relative", width: "100%" }}>
                      {/* SLEEK TRIGGER BOX */}
                      <div
                        onClick={() => setIsRoleUserAssignDropdownOpen(!isRoleUserAssignDropdownOpen)}
                        style={{
                          width: "100%",
                          minHeight: "44px",
                          padding: "8px 14px",
                          background: "#ffffff",
                          border: isRoleUserAssignDropdownOpen ? "2px solid #2563eb" : "1.5px solid #cbd5e1",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          boxShadow: isRoleUserAssignDropdownOpen ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
                          boxSizing: "border-box",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <User size={16} color="#2563eb" />
                          <span style={{ fontSize: "13.5px", fontWeight: "700", color: selectedUserForRole ? "#0f172a" : "#64748b" }}>
                            {loadingAssignableUsers
                              ? "Loading assignable users from backend..."
                              : selectedUserForRole
                                ? `${selectedUserForRole.first_name || selectedUserForRole.firstName || ""} ${selectedUserForRole.last_name || selectedUserForRole.lastName || ""}`.trim() + ` (${selectedUserForRole.email_address || selectedUserForRole.email || ""})`
                                : "-- Choose User --"}
                          </span>
                        </div>
                        <ChevronDown
                          size={16}
                          color="#64748b"
                          style={{
                            transform: isRoleUserAssignDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </div>

                      {/* FLOATING CUSTOM DROPDOWN POPUP MENU */}
                      {isRoleUserAssignDropdownOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 6px)",
                            left: 0,
                            right: 0,
                            background: "#ffffff",
                            border: "1.5px solid #e2e8f0",
                            borderRadius: "12px",
                            boxShadow: "0 18px 42px rgba(15, 23, 42, 0.22)",
                            zIndex: 999999,
                            maxHeight: "300px",
                            overflowY: "auto",
                          }}
                        >
                          {/* SEARCH INPUT INSIDE DROPDOWN */}
                          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc", sticky: "top" }}>
                            <input
                              placeholder="Type to search user name or email..."
                              value={roleUserSearchQuery}
                              onChange={(e) => setRoleUserSearchQuery(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: "100%", padding: "6px 10px", borderRadius: "6px", border: "1.5px solid #cbd5e1", fontSize: "12px", outline: "none", boxSizing: "border-box" }}
                            />
                          </div>

                          {/* USER OPTIONS LIST (BACKEND-DRIVEN) */}
                          {loadingAssignableUsers ? (
                            <div style={{ padding: "16px", fontSize: "12px", color: "#2563eb", textAlign: "center", fontWeight: "700", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                              <RefreshCw size={14} style={{ animation: "spin 0.85s linear infinite" }} />
                              <span>Fetching assignable users from SQL backend...</span>
                            </div>
                          ) : filteredAssignableUsers.length === 0 ? (
                            <div style={{ padding: "14px", fontSize: "12px", color: "#64748b", textAlign: "center" }}>
                              No users match your search query.
                            </div>
                          ) : (
                            filteredAssignableUsers.map((u) => {
                              const uId = u.user_id || u.userId;
                              const uName = `${u.first_name || u.firstName || ""} ${u.last_name || u.lastName || ""}`.trim() || u.email_address;
                              const isSelected = selectedUserForRole && String(selectedUserForRole.user_id || selectedUserForRole.userId) === String(uId);

                              return (
                                <div
                                  key={uId}
                                  onClick={() => {
                                    setSelectedUserForRole(u);
                                    setIsRoleUserAssignDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: "10px 14px",
                                    cursor: "pointer",
                                    background: isSelected ? "#eff6ff" : "#ffffff",
                                    borderBottom: "1px solid #f8fafc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <div>
                                    <div style={{ fontSize: "13px", fontWeight: "800", color: isSelected ? "#1d4ed8" : "#0f172a" }}>
                                      {uName}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#64748b", marginTop: "1px" }}>
                                      {u.email_address || u.email} {u.currentRoleName ? `• Current Role: [${u.currentRoleName}]` : "• (No Role Assigned)"}
                                    </div>
                                  </div>
                                  {isSelected && <Check size={14} color="#2563eb" />}
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* WARNING CALLOUT BANNER (BACKEND-DRIVEN ROLE REASSIGNMENT CHECK) */}
              {selectedUserForRole && selectedUserForRole.currentRoleName && selectedUserForRole.currentRoleId && String(selectedUserForRole.currentRoleId) !== String(targetAssignRole.roleId) && (() => {
                const uName = `${selectedUserForRole.first_name || selectedUserForRole.firstName || ""} ${selectedUserForRole.last_name || selectedUserForRole.lastName || ""}`.trim();

                return (
                  <div
                    style={{
                      background: "#fffbeb",
                      border: "1.5px solid #fcd34d",
                      borderRadius: "10px",
                      padding: "12px 14px",
                      display: "flex",
                      gap: "10px",
                      alignItems: "flex-start",
                      fontSize: "12.5px",
                      color: "#92400e",
                      lineHeight: "1.45",
                    }}
                  >
                    <AlertCircle size={18} color="#d97706" style={{ flexShrink: 0, marginTop: "2px" }} />
                    <div>
                      <strong>Role Reassignment Warning:</strong> User <strong>{uName}</strong> is currently assigned to <strong>"{selectedUserForRole.currentRoleName}"</strong>. Assigning them to <strong>"{targetAssignRole.roleName}"</strong> will replace their current role and update their hierarchy tree node.
                    </div>
                  </div>
                );
              })()}
            </div>

            <div style={s.modalFooter}>
              <button type="button" style={s.cancelBtn} onClick={() => setShowRoleUserAssignModal(false)} disabled={submitting}>
                Cancel
              </button>
              <button
                type="button"
                style={{ ...s.primaryBtn, background: "#2563eb" }}
                disabled={!selectedUserForRole || submitting}
                onClick={async () => {
                  if (!selectedUserForRole) return;
                  try {
                    setSubmitting(true);
                    const uId = selectedUserForRole.user_id || selectedUserForRole.userId;
                    await callApi(instance, accounts, "POST", "/api/departments/assign-role", {
                      userId: uId,
                      roleId: targetAssignRole.roleId,
                    });
                    showToast(`User assigned to role "${targetAssignRole.roleName}" successfully!`);
                    setShowRoleUserAssignModal(false);
                    fetchData();
                  } catch (err) {
                    showToast(err?.data?.message || err.message || "Failed to assign user to role", "error");
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {submitting ? "Assigning..." : "Confirm & Assign Role"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showAddDeptModal && (
        <div style={s.modalOverlay} onClick={() => setShowAddDeptModal(false)}>
          <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Add New Department</h3>
              <button style={s.closeBtn} onClick={() => setShowAddDeptModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreateDept} style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Department Name *</label>
                <input style={s.input} placeholder="e.g. Broker Relation" value={newDeptName} onChange={(e) => setNewDeptName(e.target.value)} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Department Code</label>
                <input style={s.input} placeholder="e.g. BROKER_RELATION" value={newDeptCode} onChange={(e) => setNewDeptCode(e.target.value)} />
              </div>
              <div style={s.modalFooter}>
                <button type="button" style={s.cancelBtn} onClick={() => setShowAddDeptModal(false)}>Cancel</button>
                <button type="submit" style={s.saveBtn} disabled={submitting}>{submitting ? "Saving..." : "Create Department"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= MODAL 2: MANAGE DYNAMIC ROLES ================= */}
      {showRolesModal && (
        <div style={s.modalOverlay} onClick={() => setShowRolesModal(false)}>
          <div style={{ ...s.modalBox, width: "500px" }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Dynamic Role Hierarchy Configuration</h3>
              <button style={s.closeBtn} onClick={() => setShowRolesModal(false)}><X size={18} /></button>
            </div>
            <div style={s.modalBody}>
              {/* CREATE NEW DYNAMIC ROLE FORM SECTION */}
              <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "18px" }}>
                <div style={{ fontSize: "13px", fontWeight: "800", color: "#0f172a", marginBottom: "10px" }}>
                  + Create New Custom Hierarchy Role
                </div>
                <form onSubmit={handleCreateRole} style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <div style={{ flex: 2 }}>
                      <label style={{ fontSize: "11px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "3px" }}>Role Name *</label>
                      <input
                        style={s.input}
                        placeholder="e.g. Vice President, Team Lead"
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: "11px", fontWeight: "700", color: "#475569", display: "block", marginBottom: "3px" }}>Reports To Role</label>
                      <select
                        style={s.input}
                        value={newRoleParentId}
                        onChange={(e) => setNewRoleParentId(e.target.value)}
                      >
                        <option value="">-- Top Level Role --</option>
                        {hierarchyRoles.map((r) => (
                          <option key={r.roleId} value={r.roleId}>{r.roleName}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#ffffff", padding: "8px 12px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#334155" }}>Allow Multi-Department Assignment?</span>
                    <input
                      type="checkbox"
                      style={{ width: "16px", height: "16px", cursor: "pointer" }}
                      checked={newRoleMultiDept}
                      onChange={(e) => setNewRoleMultiDept(e.target.checked)}
                    />
                  </div>

                  <button type="submit" style={{ ...s.saveBtn, width: "100%", padding: "9px" }} disabled={submitting}>
                    {submitting ? "Saving..." : "+ Add Dynamic Hierarchy Role"}
                  </button>
                </form>
              </div>

              {/* LIST OF EXISTING DYNAMIC HIERARCHY ROLES */}
              <div style={{ fontSize: "12px", fontWeight: "800", color: "#475569", marginBottom: "8px" }}>
                Active Dynamic Roles ({hierarchyRoles.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "220px", overflowY: "auto" }}>
                {hierarchyRoles.map((role) => (
                  <div key={role.roleId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#ffffff", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: "800", color: "#0f172a" }}>
                        {role.roleName} <span style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>[{role.roleCode}]</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "11px", fontWeight: "700", padding: "3px 8px", borderRadius: "6px", background: role.allowMultiDept ? "#dbeafe" : "#f1f5f9", color: role.allowMultiDept ? "#1d4ed8" : "#475569" }}>
                        {role.allowMultiDept ? "Multi-Dept Allowed" : "1 Dept Strictly"}
                      </span>
                      {!hierarchyRoles.some((r) => r.parentRoleId === role.roleId) && (
                        <button
                          title="Delete Independent Role"
                          style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: "6px", padding: "4px 8px", fontSize: "11px", fontWeight: "800", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}
                          onClick={() => setRoleToDelete(role)}
                        >
                          <Trash2 size={12} color="#dc2626" /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL: DELETE INDEPENDENT DYNAMIC ROLE CONFIRMATION ================= */}
      {roleToDelete && (
        <div style={s.modalOverlay} onClick={() => setRoleToDelete(null)}>
          <div style={{ ...s.modalBox, maxWidth: "440px" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...s.modalHeader, borderBottom: "1.5px solid #fee2e2" }}>
              <h3 style={{ ...s.modalTitle, color: "#991b1b", display: "flex", alignItems: "center", gap: "8px" }}>
                <Trash2 size={20} color="#dc2626" /> Delete Independent Dynamic Role
              </h3>
              <button style={s.closeBtn} onClick={() => setRoleToDelete(null)}><X size={18} /></button>
            </div>
            <div style={s.modalBody}>
              <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", padding: "16px", borderRadius: "12px", marginBottom: "20px" }}>
                <div style={{ fontSize: "14px", fontWeight: "800", color: "#991b1b" }}>
                  Are you sure you want to delete role <u>{roleToDelete.roleName}</u>?
                </div>
                <div style={{ fontSize: "12.5px", color: "#7f1d1d", marginTop: "6px", lineHeight: "1.5" }}>
                  This action will remove the role configuration from the hierarchy tree.
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button style={s.cancelBtn} onClick={() => setRoleToDelete(null)}>
                  Cancel
                </button>
                <button
                  style={s.dangerBtn}
                  disabled={isDeletingRole}
                  onClick={async () => {
                    try {
                      setIsDeletingRole(true);
                      const currentUserName = accounts && accounts[0] ? accounts[0].name || accounts[0].username : "Admin User";
                      const currentUserId = accounts && accounts[0] ? accounts[0].localAccountId || accounts[0].homeAccountId : "Admin User";

                      await callApi(instance, accounts, "DELETE", `/api/departments/roles/${roleToDelete.roleId}`, {
                        deletedBy: currentUserId,
                        deletedByName: currentUserName,
                      });

                      showToast(`Role '${roleToDelete.roleName}' deleted successfully.`);
                      setRoleToDelete(null);
                      setSelectedRole(null);
                      fetchData();
                    } catch (err) {
                      showToast(err?.data?.message || err?.message || "Failed to delete role", "error");
                    } finally {
                      setIsDeletingRole(false);
                    }
                  }}
                >
                  {isDeletingRole ? "Deleting..." : "🗑️ Confirm Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL 3: MAP / ASSIGN USER HIERARCHY ================= */}
      {showAssignModal && (
        <div style={{ ...s.modalOverlay, zIndex: 1000005 }} onClick={handleCloseAssignModal}>
          <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {isAssignAccessOnly
                  ? "Assign Department Access"
                  : assignReportingToId
                    ? "Add Subordinate Member under Manager"
                    : "Map User Hierarchy & Departments"}
              </h3>
              <button style={s.closeBtn} onClick={handleCloseAssignModal}><X size={18} /></button>
            </div>

            <form onSubmit={handleAssignUserSubmit} style={s.modalBody}>
              {isAssignAccessOnly && (() => {
                const selectedUserObj = allUsers.find((u) => String(u.userId || u.user_id) === String(assignUserId));
                const mgrUser = allUsers.find((u) => String(u.userId || u.user_id) === String(assignReportingToId));
                const mgrName = mgrUser ? `${mgrUser.firstName || mgrUser.first_name || ""} ${mgrUser.lastName || mgrUser.last_name || ""}`.trim() : null;

                return (
                  <div style={{ background: "#f8fafc", border: "1.5px solid #cbd5e1", padding: "12px 14px", borderRadius: "10px", marginBottom: "14px", fontSize: "12.5px" }}>
                    <div style={{ fontWeight: "800", color: "#0f172a", fontSize: "14px", marginBottom: "4px" }}>
                      👤 {selectedUserObj ? `${selectedUserObj.firstName || selectedUserObj.first_name || ""} ${selectedUserObj.lastName || selectedUserObj.last_name || ""}`.trim() : "User"}
                      <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "600", marginLeft: "6px" }}>
                        ({selectedUserObj?.email || selectedUserObj?.email_address || ""})
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", fontSize: "12px", color: "#475569", marginTop: "6px" }}>
                      <span>🛡️ Role: <strong>{selectedUserObj?.currentRoleName || "Team Member"}</strong></span>
                      {mgrName && <span>👔 Reports To: <strong>{mgrName}</strong></span>}
                    </div>
                  </div>
                );
              })()}

              {/* 1. REPORTING TO (MANAGER / SUPERIOR) AT TOP */}
              {!isAssignAccessOnly && (
                <div style={{ ...s.formGroup, position: "relative" }}>
                  <label style={s.label}>Reporting To (Manager / Superior)</label>
                  {(() => {
                    // Directly consume candidate superior managers fetched from SQL DB (/api/departments/assignable-superiors)
                    const mgrCandidates = candidateSuperiorUsers;

                    const filteredMgrs = mgrCandidates.filter((u) => {
                      if (!assignReportingToSearchQuery.trim()) return true;
                      const q = assignReportingToSearchQuery.toLowerCase();
                      const name = `${u.firstName || u.first_name || ""} ${u.lastName || u.last_name || ""}`.toLowerCase();
                      const email = (u.email || u.email_address || u.userEmail || u.user_email || "").toLowerCase();
                      return name.includes(q) || email.includes(q);
                    });

                    const selectedMgrObj = allUsers.find(
                      (u) => String(u.userId || u.user_id) === String(assignReportingToId)
                    );

                    return (
                      <div style={{ position: "relative", width: "100%" }}>
                        {/* DIRECT SEARCH INPUT FIELD */}
                        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                          <UserCheck size={16} color="#2563eb" style={{ position: "absolute", left: 12, pointerEvents: "none" }} />
                          <input
                            type="text"
                            placeholder="-- Top Level (No Parent) --"
                            value={
                              isAssignReportingToDropdownOpen
                                ? assignReportingToSearchQuery
                                : selectedMgrObj
                                  ? `${selectedMgrObj.firstName || selectedMgrObj.first_name || ""} ${selectedMgrObj.lastName || selectedMgrObj.last_name || ""}`.trim() + ` (${selectedMgrObj.email || selectedMgrObj.email_address || ""})`
                                  : assignReportingToSearchQuery
                            }
                            onFocus={() => {
                              setIsAssignReportingToDropdownOpen(true);
                              setIsAssignUserDropdownOpen(false);
                            }}
                            onChange={(e) => {
                              const val = e.target.value;
                              setAssignReportingToSearchQuery(val);
                              setIsAssignReportingToDropdownOpen(true);
                              setIsAssignUserDropdownOpen(false);
                              if (!val.trim()) {
                                setAssignReportingToId("");
                              }
                            }}
                            style={{
                              width: "100%",
                              minHeight: "42px",
                              padding: "8px 36px 8px 36px",
                              background: "#ffffff",
                              border: isAssignReportingToDropdownOpen ? "2px solid #2563eb" : "1.5px solid #cbd5e1",
                              borderRadius: "10px",
                              fontSize: "13px",
                              fontWeight: "700",
                              color: selectedMgrObj || assignReportingToSearchQuery ? "#0f172a" : "#64748b",
                              outline: "none",
                              boxShadow: isAssignReportingToDropdownOpen ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
                              boxSizing: "border-box",
                              transition: "all 0.15s ease",
                            }}
                          />
                          <ChevronDown
                            size={16}
                            color="#64748b"
                            onClick={() => {
                              const nextState = !isAssignReportingToDropdownOpen;
                              setIsAssignReportingToDropdownOpen(nextState);
                              if (nextState) setIsAssignUserDropdownOpen(false);
                            }}
                            style={{
                              position: "absolute",
                              right: 12,
                              cursor: "pointer",
                              transform: isAssignReportingToDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </div>

                        {/* FLOATING MENU */}
                        {isAssignReportingToDropdownOpen && (
                          <div
                            style={{
                              position: "absolute",
                              top: "calc(100% + 4px)",
                              left: 0,
                              right: 0,
                              background: "#ffffff",
                              border: "1.5px solid #e2e8f0",
                              borderRadius: "12px",
                              boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18)",
                              zIndex: 99999,
                              maxHeight: "250px",
                              overflowY: "auto",
                            }}
                          >
                            <div
                              onClick={() => {
                                setAssignReportingToId("");
                                setAssignReportingToSearchQuery("");
                                setIsAssignReportingToDropdownOpen(false);
                              }}
                              style={{
                                padding: "10px 14px",
                                cursor: "pointer",
                                background: !assignReportingToId ? "#eff6ff" : "#ffffff",
                                borderBottom: "1px solid #f8fafc",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                fontSize: "13px",
                                fontWeight: "700",
                                color: !assignReportingToId ? "#1d4ed8" : "#475569",
                              }}
                            >
                              <span>-- Top Level (No Parent) --</span>
                              {!assignReportingToId && <Check size={14} color="#2563eb" />}
                            </div>

                            {filteredMgrs.map((u) => {
                              const uId = u.userId || u.user_id;
                              const uName = `${u.firstName || u.first_name || ""} ${u.lastName || u.last_name || ""}`.trim();
                              const isSelected = String(assignReportingToId) === String(uId);

                              return (
                                <div
                                  key={uId}
                                  onClick={() => {
                                    const managerIdStr = String(uId);
                                    setAssignReportingToId(managerIdStr);
                                    setAssignReportingToSearchQuery(uName);
                                    setIsAssignReportingToDropdownOpen(false);

                                    // Instant zero-delay local state update from allUsers & hierarchyRoles
                                    const targetMgr = allUsers.find((m) => String(m.userId || m.user_id) === managerIdStr);
                                    if (targetMgr) {
                                      const mgrDepts = targetMgr.departments || [];
                                      if (mgrDepts.length > 0) {
                                        setManagerDepartments(mgrDepts);
                                      }

                                      const mgrRoleId = targetMgr.roleId || targetMgr.role_id || targetMgr.currentRoleId;
                                      const mgrRoleObj = hierarchyRoles.find(
                                        (r) => String(r.roleId) === String(mgrRoleId) || (targetMgr.currentRoleName && r.roleName === targetMgr.currentRoleName)
                                      );
                                      if (mgrRoleObj) {
                                        const childRole = hierarchyRoles.find(
                                          (r) => String(r.parentRoleId || r.parent_role_id) === String(mgrRoleObj.roleId)
                                        );
                                        if (childRole) {
                                          setAssignRoleId(String(childRole.roleId));
                                          setSubordinateRoleInfo(childRole);
                                        }
                                      }
                                    }
                                  }}
                                  style={{
                                    padding: "10px 14px",
                                    cursor: "pointer",
                                    background: isSelected ? "#eff6ff" : "#ffffff",
                                    borderBottom: "1px solid #f8fafc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <div>
                                    <div style={{ fontSize: "13px", fontWeight: "800", color: isSelected ? "#1d4ed8" : "#0f172a" }}>
                                      {uName}
                                    </div>
                                    <div style={{ fontSize: "11px", color: "#64748b", marginTop: "1px" }}>
                                      {u.email || u.email_address} {u.currentRoleName ? `• [${u.currentRoleName}]` : ""}
                                    </div>
                                  </div>
                                  {isSelected && <Check size={14} color="#2563eb" />}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}



              {/* 3. SELECT USER */}
              {!isAssignAccessOnly && (
                <div style={{ ...s.formGroup, position: "relative" }}>
                  <label style={s.label}>Select User *</label>
                  {(() => {
                    const getAllMappedUserIds = (treeNodes) => {
                      const ids = new Set();
                      const traverse = (nodes) => {
                        (nodes || []).forEach((n) => {
                          const uid = n.userId || n.user_id || n.id;
                          if (uid) ids.add(String(uid));
                          if (n.subordinates && n.subordinates.length > 0) traverse(n.subordinates);
                          if (n.children && n.children.length > 0) traverse(n.children);
                        });
                      };
                      traverse(treeNodes);
                      return ids;
                    };

                    const mappedUserIdsSet = getAllMappedUserIds(reportingTree);
                    const candidateUsersList = assignReportingToId ? subordinateUsers : assignableUsers;

                    const unmappedUsersList = candidateUsersList.filter((u) => {
                      const uIdStr = String(u.userId || u.user_id);
                      if (assignUserId && uIdStr === String(assignUserId)) return true;
                      if (mappedUserIdsSet.has(uIdStr)) return false;
                      return !u.isMapped && !u.currentReportingToName && !u.reporting_to_id;
                    });

                    const filteredUsers = unmappedUsersList.filter((u) => {
                      if (!assignUserSearchQuery.trim()) return true;
                      const q = assignUserSearchQuery.toLowerCase();
                      const name = `${u.firstName || u.first_name || ""} ${u.lastName || u.last_name || ""}`.toLowerCase();
                      const email = (u.email || u.email_address || u.userEmail || u.user_email || "").toLowerCase();
                      return name.includes(q) || email.includes(q);
                    });

                    const selectedUserObj = unmappedUsersList.find((u) => String(u.userId || u.user_id) === String(assignUserId));

                    return (
                      <div style={{ position: "relative", width: "100%" }}>
                        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                          <User size={16} color="#2563eb" style={{ position: "absolute", left: 12, pointerEvents: "none" }} />
                          <input
                            type="text"
                            placeholder="-- Choose User --"
                            value={
                              isAssignUserDropdownOpen
                                ? assignUserSearchQuery
                                : selectedUserObj
                                  ? `${selectedUserObj.firstName || selectedUserObj.first_name || ""} ${selectedUserObj.lastName || selectedUserObj.last_name || ""}`.trim() + ` (${selectedUserObj.email || selectedUserObj.email_address || ""})`
                                  : assignUserSearchQuery
                            }
                            onFocus={() => {
                              setIsAssignUserDropdownOpen(true);
                              setIsAssignReportingToDropdownOpen(false);
                            }}
                            onChange={(e) => {
                              const val = e.target.value;
                              setAssignUserSearchQuery(val);
                              setIsAssignUserDropdownOpen(true);
                              setIsAssignReportingToDropdownOpen(false);
                              if (!val.trim()) {
                                setAssignUserId("");
                              }
                            }}
                            style={{
                              width: "100%",
                              minHeight: "42px",
                              padding: "8px 36px 8px 36px",
                              background: "#ffffff",
                              border: isAssignUserDropdownOpen ? "2px solid #2563eb" : "1.5px solid #cbd5e1",
                              borderRadius: "10px",
                              fontSize: "13px",
                              fontWeight: "700",
                              color: selectedUserObj || assignUserSearchQuery ? "#0f172a" : "#64748b",
                              outline: "none",
                              boxShadow: isAssignUserDropdownOpen ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
                              boxSizing: "border-box",
                              transition: "all 0.15s ease",
                            }}
                          />
                          <ChevronDown
                            size={16}
                            color="#64748b"
                            onClick={() => {
                              const nextState = !isAssignUserDropdownOpen;
                              setIsAssignUserDropdownOpen(nextState);
                              if (nextState) setIsAssignReportingToDropdownOpen(false);
                            }}
                            style={{
                              position: "absolute",
                              right: 12,
                              cursor: "pointer",
                              transform: isAssignUserDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </div>

                        {isAssignUserDropdownOpen && (
                          <div
                            style={{
                              position: "absolute",
                              top: "calc(100% + 4px)",
                              left: 0,
                              right: 0,
                              background: "#ffffff",
                              border: "1.5px solid #e2e8f0",
                              borderRadius: "12px",
                              boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18)",
                              zIndex: 99999,
                              maxHeight: "250px",
                              overflowY: "auto",
                            }}
                          >
                            {filteredUsers.length === 0 ? (
                              <div style={{ padding: "14px", fontSize: "12px", color: "#64748b", textAlign: "center" }}>
                                No users match your search query.
                              </div>
                            ) : (
                              filteredUsers.map((u) => {
                                const uId = u.userId || u.user_id;
                                const uName = `${u.firstName || u.first_name || ""} ${u.lastName || u.last_name || ""}`.trim();
                                const isSelected = String(assignUserId) === String(uId);

                                return (
                                  <div
                                    key={uId}
                                    onClick={() => {
                                      setAssignUserId(String(uId));
                                      setAssignUserSearchQuery(uName);
                                      setIsAssignUserDropdownOpen(false);

                                      // Update assignRoleId instantly based on selected user's role
                                      const targetUserObj = allUsers.find((user) => String(user.userId || user.user_id) === String(uId));
                                      const userRoleId = u.currentRoleId || targetUserObj?.roleId || targetUserObj?.role_id || targetUserObj?.currentRoleId;
                                      if (userRoleId) {
                                        setAssignRoleId(String(userRoleId));
                                      }
                                    }}
                                    style={{
                                      padding: "10px 14px",
                                      cursor: "pointer",
                                      background: isSelected ? "#eff6ff" : "#ffffff",
                                      borderBottom: "1px solid #f8fafc",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <div>
                                      <div style={{ fontSize: "13px", fontWeight: "800", color: isSelected ? "#1d4ed8" : "#0f172a" }}>
                                        {uName}
                                      </div>
                                      <div style={{ fontSize: "11px", color: "#64748b", marginTop: "1px", display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
                                        <span>{u.email || u.email_address || u.userEmail || u.user_email}</span>
                                        <span>{u.currentRoleName ? `• Current Role: [${u.currentRoleName}]` : "• (No Role Assigned)"}</span>
                                        {u.currentReportingToName && (
                                          <span style={{ fontSize: "10.5px", fontWeight: "700", color: "#b45309", background: "#fef3c7", padding: "1px 6px", borderRadius: "8px", border: "1px solid #fde68a" }}>
                                            🔄 Reports to: {u.currentReportingToName} (Will be moved)
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {isSelected && <Check size={14} color="#2563eb" />}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* 4. SELECT DYNAMIC ROLE */}
              {!isAssignAccessOnly && (() => {
                const selectedUserObj = allUsers.find(
                  (u) => String(u.userId || u.user_id) === String(assignUserId)
                );
                const userAssignedRole = hierarchyRoles.find(
                  (r) =>
                    (r.users || []).some((m) => String(m.userId || m.user_id) === String(assignUserId)) ||
                    (selectedUserObj?.currentRoleName && r.roleName === selectedUserObj.currentRoleName)
                );
                const topRootRole = hierarchyRoles.find((r) => !r.parentRoleId && !r.parent_role_id) || hierarchyRoles[0];
                const isTopLevelSelected = !assignReportingToId;
                const displayRoleObj = isTopLevelSelected
                  ? topRootRole
                  : (hierarchyRoles.find((r) => String(r.roleId) === String(assignRoleId)) || userAssignedRole);

                return (
                  <div style={{ ...s.formGroup, position: "relative" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                      <label style={s.label}>Select Dynamic Role (Optional)</label>
                      {displayRoleObj && (
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#1e40af", background: "#eff6ff", padding: "3px 10px", borderRadius: "12px", border: "1px solid #bfdbfe", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                          <ShieldCheck size={13} color="#2563eb" />
                          <span>{displayRoleObj.roleName}</span>
                        </span>
                      )}
                    </div>

                    {displayRoleObj ? (
                      <div
                        style={{
                          width: "100%",
                          minHeight: "42px",
                          padding: "8px 12px",
                          background: "#f8fafc",
                          border: "1.5px solid #cbd5e1",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          color: "#334155",
                          fontWeight: "700",
                          fontSize: "13px",
                          boxSizing: "border-box",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Shield size={16} color="#64748b" />
                          <span>{displayRoleObj.roleName} ({displayRoleObj.allowMultiDept ? "Multi-Dept" : "1 Dept Only"})</span>
                        </div>
                        <span style={{ fontSize: "11px", color: "#475569", fontWeight: "700", background: "#e2e8f0", padding: "2px 8px", borderRadius: "6px", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <Lock size={12} color="#64748b" />
                          <span>System Assigned</span>
                        </span>
                      </div>
                    ) : (
                      <div style={{ position: "relative", width: "100%" }}>
                        <div
                          onClick={() => setIsAssignRoleDropdownOpen(!isAssignRoleDropdownOpen)}
                          style={{
                            width: "100%",
                            minHeight: "42px",
                            padding: "8px 12px",
                            background: "#ffffff",
                            border: isAssignRoleDropdownOpen ? "2px solid #2563eb" : "1.5px solid #cbd5e1",
                            borderRadius: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            cursor: "pointer",
                            boxShadow: isAssignRoleDropdownOpen ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
                            boxSizing: "border-box",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <Shield size={16} color="#2563eb" />
                            <span style={{ fontSize: "13px", fontWeight: "700", color: displayRoleObj ? "#0f172a" : "#64748b" }}>
                              {displayRoleObj
                                ? `${displayRoleObj.roleName} (${displayRoleObj.allowMultiDept ? "Multi-Dept" : "1 Dept Only"})`
                                : "-- Select Role (Optional) --"}
                            </span>
                          </div>
                          <ChevronDown
                            size={16}
                            color="#64748b"
                            style={{
                              transform: isAssignRoleDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </div>

                        {isAssignRoleDropdownOpen && (
                          <div
                            style={{
                              position: "absolute",
                              top: "calc(100% + 4px)",
                              left: 0,
                              right: 0,
                              background: "#ffffff",
                              border: "1.5px solid #e2e8f0",
                              borderRadius: "12px",
                              boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18)",
                              zIndex: 99999,
                              maxHeight: "250px",
                              overflowY: "auto",
                            }}
                          >
                            <div
                              onClick={() => {
                                setAssignRoleId("");
                                setIsAssignRoleDropdownOpen(false);
                              }}
                              style={{
                                padding: "10px 14px",
                                cursor: "pointer",
                                background: !assignRoleId ? "#eff6ff" : "#ffffff",
                                borderBottom: "1px solid #f8fafc",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                fontSize: "13px",
                                fontWeight: "700",
                                color: !assignRoleId ? "#1d4ed8" : "#475569",
                              }}
                            >
                              <span>-- Select Role (Optional) --</span>
                              {!assignRoleId && <Check size={14} color="#2563eb" />}
                            </div>

                            {hierarchyRoles.map((r) => {
                              const isSelected = String(assignRoleId) === String(r.roleId);

                              return (
                                <div
                                  key={r.roleId}
                                  onClick={() => {
                                    setAssignRoleId(String(r.roleId));
                                    setIsAssignRoleDropdownOpen(false);
                                  }}
                                  style={{
                                    padding: "10px 14px",
                                    cursor: "pointer",
                                    background: isSelected ? "#eff6ff" : "#ffffff",
                                    borderBottom: "1px solid #f8fafc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <span style={{ fontSize: "13px", fontWeight: "800", color: isSelected ? "#1d4ed8" : "#0f172a" }}>
                                      {r.roleName}
                                    </span>
                                    <span style={{ fontSize: "10.5px", fontWeight: "700", background: r.allowMultiDept ? "#eff6ff" : "#fffbeb", color: r.allowMultiDept ? "#2563eb" : "#d97706", padding: "1px 6px", borderRadius: "6px", border: `1px solid ${r.allowMultiDept ? "#bfdbfe" : "#fde68a"}` }}>
                                      {r.allowMultiDept ? "Multi-Dept" : "1 Dept Only"}
                                    </span>
                                  </div>
                                  {isSelected && <Check size={14} color="#2563eb" />}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 5. SELECT DEPARTMENT(S) */}
              {(() => {
                const targetUserObj = allUsers.find((u) => String(u.userId || u.user_id) === String(assignUserId));
                const activeRoleObj =
                  hierarchyRoles.find((r) => String(r.roleId) === String(assignRoleId)) ||
                  subordinateRoleInfo ||
                  (targetUserObj?.currentRoleName ? hierarchyRoles.find((r) => r.roleName === targetUserObj.currentRoleName) : null);

                const isMultiDeptAllowed = activeRoleObj
                  ? Boolean(activeRoleObj.allowMultiDept ?? activeRoleObj.allow_multi_dept)
                  : targetUserObj?.allowMultiDept ?? true;

                // Restrict to manager's assigned departments ONLY if the role is 1-Dept (Team Member) AND has a manager
                const isSubordinateTarget = !isMultiDeptAllowed && Boolean(assignReportingToId);

                const managerObj = allUsers.find((u) => String(u.userId || u.user_id) === String(assignReportingToId));
                const managerDeptList = managerDepartments.length > 0
                  ? managerDepartments
                  : (managerObj?.departments || []);

                const displayDeptList = isSubordinateTarget ? managerDeptList : departments;

                return (
                  <div style={s.formGroup}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
                      <label style={s.label}>
                        {isSubordinateTarget
                          ? "Select Department (Manager's Department Only)"
                          : isMultiDeptAllowed
                            ? "Select Department(s) (Optional)"
                            : "Select Department (1 Dept Only)"}
                      </label>
                      {!isMultiDeptAllowed && (
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#b45309", background: "#fef3c7", padding: "3px 10px", borderRadius: "12px", border: "1px solid #fde68a", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                          <AlertCircle size={13} color="#d97706" />
                          <span>Single-Dept Enforced ({activeRoleObj?.roleName || activeRoleObj?.role_name || "Role"})</span>
                        </span>
                      )}
                    </div>

                    {isSubordinateTarget && loadingSubordinateUsers ? (
                      <div style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", padding: "14px", borderRadius: "10px", fontSize: "12.5px", color: "#2563eb", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
                        <RefreshCw size={15} color="#2563eb" style={{ animation: "spin 0.8s linear infinite" }} />
                        <span>Loading manager's assigned departments...</span>
                      </div>
                    ) : isSubordinateTarget && displayDeptList.length === 0 ? (
                      <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", padding: "12px 14px", borderRadius: "10px", fontSize: "12.5px", color: "#991b1b", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
                        <AlertCircle size={16} color="#dc2626" style={{ flexShrink: 0 }} />
                        <span>Please assign the department of the manager first.</span>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "200px", overflowY: "auto", padding: "4px" }}>
                        {displayDeptList.map((d) => {
                          const idStr = String(d.departmentId || d.department_id);
                          const dName = d.departmentName || d.department_name;
                          const isChecked = assignDeptIds.includes(idStr);

                          return (
                            <div
                              key={idStr}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "9px 14px",
                                background: isChecked ? "#f0f9ff" : "#f8fafc",
                                border: `1.5px solid ${isChecked ? "#bfdbfe" : "#e2e8f0"}`,
                                borderRadius: "12px",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                boxShadow: isChecked ? "0 2px 6px rgba(37, 99, 235, 0.08)" : "none",
                              }}
                              onClick={async () => {
                                const willBeChecked = !isChecked;
                                let nextIds = [];

                                const updateSelectedNodeDepts = (ids) => {
                                  if (!selectedNode) return;
                                  const newDeptObjs = ids.map((id) => {
                                    const foundDept = departments.find((d) => String(d.departmentId || d.department_id) === String(id));
                                    return {
                                      departmentId: id,
                                      department_id: id,
                                      departmentName: foundDept ? (foundDept.departmentName || foundDept.department_name) : `Dept ${id}`,
                                      department_name: foundDept ? (foundDept.departmentName || foundDept.department_name) : `Dept ${id}`,
                                    };
                                  });
                                  setSelectedNode((prev) => (prev ? { ...prev, departments: newDeptObjs } : null));
                                };

                                if (willBeChecked) {
                                  nextIds = isMultiDeptAllowed ? [...assignDeptIds, idStr] : [idStr];
                                  setAssignDeptIds(nextIds);
                                  updateSelectedNodeDepts(nextIds);

                                  // Automatic save in background for Toggle ON!
                                  const uid = assignUserId || targetUserObj?.userId || targetUserObj?.user_id;
                                  if (uid && String(uid) !== "undefined" && String(uid) !== "") {
                                    try {
                                      setCheckingDeptId(idStr);
                                      await callApi(instance, accounts, "POST", "/api/departments/assign-user", {
                                        userId: uid,
                                        roleId: isAssignAccessOnly ? null : assignRoleId,
                                        departmentIds: nextIds,
                                        reportingToId: assignReportingToId || null,
                                      });
                                      showToast("Department access updated & saved automatically!");
                                      fetchData();
                                    } catch (err) {
                                      showToast(err?.data?.message || err?.message || "Failed to auto-save department assignment", "error");
                                    } finally {
                                      setCheckingDeptId(null);
                                    }
                                  }
                                } else {
                                  nextIds = assignDeptIds.filter((i) => i !== idStr);
                                  setAssignDeptIds(nextIds);
                                  updateSelectedNodeDepts(nextIds);

                                  // Instant real-time conflict check on toggle OFF!
                                  const uid = assignUserId || targetUserObj?.userId || targetUserObj?.user_id;
                                  if (uid && String(uid) !== "undefined" && String(uid) !== "") {
                                    try {
                                      setCheckingDeptId(idStr);
                                      const checkRes = await callApi(instance, accounts, "POST", "/api/departments/check-conflict", {
                                        userId: uid,
                                        departmentIds: nextIds,
                                        previousDepartmentIds: assignDeptIds,
                                      });
                                      const conflictData = checkRes?.data?.data || checkRes?.data;
                                      if (conflictData && conflictData.hasConflict && conflictData.affectedSubordinates?.length > 0) {
                                        const targetUserObjResolved = allUsers.find((u) => String(u.userId || u.user_id) === String(uid)) || targetUserObj;
                                        setDeptConflictData({
                                          managerUser: targetUserObjResolved,
                                          newDeptIds: nextIds,
                                          previousDeptIds: [...assignDeptIds],
                                          affectedSubordinates: conflictData.affectedSubordinates,
                                        });
                                        setShowAssignModal(false);
                                        setShowDeptConflictModal(true);
                                      } else {
                                        // No conflict -> Automatic save in background for Toggle OFF!
                                        await callApi(instance, accounts, "POST", "/api/departments/assign-user", {
                                          userId: uid,
                                          roleId: assignRoleId,
                                          departmentIds: nextIds,
                                          reportingToId: assignReportingToId || null,
                                        });
                                        showToast("Department access updated & saved automatically!");
                                        fetchData();
                                      }
                                    } catch (err) {
                                      console.error("Real-time toggle conflict check error:", err);
                                    } finally {
                                      setCheckingDeptId(null);
                                    }
                                  }
                                }
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <Building2 size={16} color={isChecked ? "#2563eb" : "#64748b"} />
                                <span style={{ fontSize: "13px", fontWeight: "700", color: isChecked ? "#0f172a" : "#64748b" }}>
                                  {dName}
                                </span>
                              </div>

                              {/* MODERN iOS/SAAS STYLE TOGGLE SWITCH WITH LOADER BADGE */}
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {checkingDeptId === idStr ? (
                                  <span style={{ fontSize: "11px", fontWeight: "800", color: "#2563eb", display: "flex", alignItems: "center", gap: "4px" }}>
                                    <RefreshCw size={13} color="#2563eb" style={{ animation: "spin 0.8s linear infinite" }} />
                                    Saving...
                                  </span>
                                ) : (
                                  <span style={{ fontSize: "10.5px", fontWeight: "800", color: isChecked ? "#2563eb" : "#94a3b8", letterSpacing: "0.4px" }}>
                                    {isChecked ? "ACCESS ON" : "OFF"}
                                  </span>
                                )}
                                <div
                                  style={{
                                    width: "38px",
                                    height: "20px",
                                    borderRadius: "20px",
                                    background: checkingDeptId === idStr ? "#93c5fd" : (isChecked ? "#2563eb" : "#cbd5e1"),
                                    position: "relative",
                                    transition: "background 0.25s ease",
                                    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.15)",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      borderRadius: "50%",
                                      background: "#ffffff",
                                      position: "absolute",
                                      top: "2px",
                                      left: isChecked ? "20px" : "2px",
                                      transition: "left 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* MANAGER RE-ASSIGNMENT WARNING */}
              {(() => {
                const targetList = assignReportingToId ? subordinateUsers : allUsers;
                const selectedUserObj = targetList.find((u) => String(u.userId || u.user_id) === String(assignUserId));
                if (!selectedUserObj || !selectedUserObj.currentReportingToName) return null;

                return (
                  <div
                    style={{
                      background: "#fffbeb",
                      border: "1.5px solid #fcd34d",
                      borderRadius: "10px",
                      padding: "12px 14px",
                      display: "flex",
                      gap: "10px",
                      alignItems: "flex-start",
                      fontSize: "12.5px",
                      color: "#92400e",
                      lineHeight: "1.45",
                    }}
                  >
                    <AlertCircle size={18} color="#d97706" style={{ flexShrink: 0, marginTop: "2px" }} />
                    <div>
                      <strong>Manager Re-assignment Warning:</strong> User <strong>{selectedUserObj.firstName || selectedUserObj.first_name} {selectedUserObj.lastName || selectedUserObj.last_name}</strong> currently reports to <strong>"{selectedUserObj.currentReportingToName}"</strong>.
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const hasAssignedDepts = assignDeptIds.length > 0;

                return (
                  <div style={{ ...s.modalFooter, justifyContent: "flex-end", gap: "10px", alignItems: "center", marginTop: "10px" }}>
                    {!hasAssignedDepts && (
                      <button
                        type="button"
                        style={{ ...s.secondaryBtn, padding: "10px 18px", fontSize: "13px", fontWeight: "700" }}
                        disabled={submitting}
                        onClick={handleCloseAssignModal}
                      >
                        Cancel
                      </button>
                    )}

                    {hasAssignedDepts ? (
                      <button
                        type="button"
                        style={{ ...s.primaryBtn, padding: "10px 20px", fontSize: "13px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px", background: "#2563eb" }}
                        disabled={submitting}
                        onClick={async () => {
                          handleCloseAssignModal();
                          await fetchData();
                        }}
                      >
                        <CheckCircle2 size={15} color="#ffffff" />
                        Done & Close
                      </button>
                    ) : (
                      <button
                        type="submit"
                        style={{ ...s.primaryBtn, padding: "10px 20px", fontSize: "13px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px" }}
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <RefreshCw size={14} color="#ffffff" style={{ animation: "spin 0.8s linear infinite" }} />
                            Saving...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 size={15} color="#ffffff" />
                            Save Hierarchy & Assign
                          </>
                        )}
                      </button>
                    )}
                  </div>
                );
              })()}
            </form>
          </div>
        </div>
      )}

      {/* ================= MODAL: DEPARTMENT REMOVAL CONFLICT RESOLUTION MODAL ================= */}
      {showDeptConflictModal && deptConflictData && (
        <div style={{ ...s.modalOverlay, zIndex: 2000000 }} onClick={handleCancelDeptConflict}>
          <div style={{ ...s.modalBox, width: "540px" }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: "#dc2626", display: "flex", alignItems: "center", gap: "8px" }}>
                <AlertCircle size={20} color="#dc2626" /> Department Conflict Resolution
              </h3>
              <button style={s.closeBtn} onClick={handleCancelDeptConflict}>
                <X size={18} />
              </button>
            </div>

            <div style={{ ...s.modalBody, gap: "16px" }}>
              <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", padding: "12px 14px", borderRadius: "10px", fontSize: "12.5px", color: "#991b1b", lineHeight: "1.45" }}>
                <strong>Department Access Change:</strong> Manager <strong>{deptConflictData.managerUser?.firstName || deptConflictData.managerUser?.first_name} {deptConflictData.managerUser?.lastName || deptConflictData.managerUser?.last_name}</strong> is losing department access. The following subordinate member(s) belong to the unassigned department:
              </div>

              {(() => {
                // Group affected subordinates by conflict department ID
                const deptGroupsMap = new Map();
                (deptConflictData.affectedSubordinates || []).forEach((item, originalIdx) => {
                  const dId = String(item.conflictDept?.departmentId || item.conflictDept?.department_id || "default");
                  const dName = item.conflictDept?.departmentName || item.conflictDept?.department_name || "Department";

                  if (!deptGroupsMap.has(dId)) {
                    deptGroupsMap.set(dId, {
                      departmentId: dId,
                      departmentName: dName,
                      availableManagers: item.availableManagers || [],
                      subEntries: [],
                    });
                  }
                  deptGroupsMap.get(dId).subEntries.push({ item, originalIdx });
                });

                const deptGroups = Array.from(deptGroupsMap.values());

                return deptGroups.map((group, grpIdx) => {
                  const hasCandidateMgrs = group.availableManagers.length > 0;
                  const firstItem = group.subEntries[0]?.item;
                  const groupAction = firstItem?.selectedAction || "move";
                  const groupTargetManagerId = firstItem?.selectedTargetManagerId || "";

                  return (
                    <div
                      key={group.departmentId || grpIdx}
                      style={{
                        background: "#ffffff",
                        border: "1.5px solid #cbd5e1",
                        borderRadius: "14px",
                        padding: "16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                        boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)",
                      }}
                    >
                      {/* DEPARTMENT BADGE & MEMBER COUNT */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f1f5f9", paddingBottom: "10px" }}>
                        <div style={{ fontSize: "14px", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                          🏢 <span>{group.departmentName}</span>
                        </div>
                        <span style={{ fontSize: "11.5px", fontWeight: "800", background: "#fef3c7", color: "#b45309", padding: "3px 10px", borderRadius: "14px", border: "1px solid #fde68a" }}>
                          {group.subEntries.length} Subordinate Member{group.subEntries.length > 1 ? "s" : ""} Affected
                        </span>
                      </div>

                      {/* LIST OF ALL AFFECTED SUBORDINATES IN THIS DEPARTMENT */}
                      <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: "11.5px", fontWeight: "800", color: "#475569", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          Subordinate Member(s) in {group.departmentName}:
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {group.subEntries.map(({ item }) => {
                            const sub = item.subordinate;
                            const subName = `${sub.firstName || sub.first_name || ""} ${sub.lastName || sub.last_name || ""}`.trim();
                            const subEmail = sub.email || sub.email_address || "";
                            return (
                              <div
                                key={sub.userId || sub.user_id}
                                style={{
                                  background: "#ffffff",
                                  border: "1px solid #cbd5e1",
                                  borderRadius: "8px",
                                  padding: "5px 10px",
                                  fontSize: "12px",
                                  fontWeight: "700",
                                  color: "#0f172a",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                                }}
                              >
                                👤 <span>{subName}</span>
                                {subEmail && <span style={{ fontSize: "10.5px", color: "#64748b", fontWeight: "500" }}>({subEmail})</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* SINGLE SET OF ACTIONS FOR ALL MEMBERS IN THIS DEPARTMENT */}
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "2px" }}>
                        {hasCandidateMgrs ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {/* MODE SELECTOR TABS: MOVE ALL TO TARGET MANAGER vs REMOVE ALL */}
                            <div style={{ display: "flex", background: "#f1f5f9", padding: "4px", borderRadius: "10px", gap: "4px" }}>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = [...deptConflictData.affectedSubordinates];
                                  group.subEntries.forEach(({ originalIdx }) => {
                                    updated[originalIdx].selectedAction = "move";
                                  });
                                  setDeptConflictData({ ...deptConflictData, affectedSubordinates: updated });
                                }}
                                style={{
                                  flex: 1,
                                  padding: "9px 12px",
                                  border: "none",
                                  borderRadius: "8px",
                                  fontSize: "12px",
                                  fontWeight: "800",
                                  cursor: "pointer",
                                  background: groupAction === "move" ? "#2563eb" : "transparent",
                                  color: groupAction === "move" ? "#ffffff" : "#475569",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                  boxShadow: groupAction === "move" ? "0 2px 6px rgba(37,99,235,0.2)" : "none",
                                }}
                              >
                                <UserCheck size={15} /> Move All to Target Manager
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  const updated = [...deptConflictData.affectedSubordinates];
                                  group.subEntries.forEach(({ originalIdx }) => {
                                    updated[originalIdx].selectedAction = "remove";
                                  });
                                  setDeptConflictData({ ...deptConflictData, affectedSubordinates: updated });
                                }}
                                style={{
                                  flex: 1,
                                  padding: "9px 12px",
                                  border: "none",
                                  borderRadius: "8px",
                                  fontSize: "12px",
                                  fontWeight: "800",
                                  cursor: "pointer",
                                  background: groupAction === "remove" ? "#dc2626" : "transparent",
                                  color: groupAction === "remove" ? "#ffffff" : "#475569",
                                  transition: "all 0.2s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "6px",
                                  boxShadow: groupAction === "remove" ? "0 2px 6px rgba(220,38,38,0.2)" : "none",
                                }}
                              >
                                <UserX size={15} /> Remove & Disconnect All
                              </button>
                            </div>

                            {/* CONDITIONAL CONTENT: MOVE TO TARGET MANAGER vs REMOVE */}
                            {groupAction === "move" ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: "8px", position: "relative", marginTop: "2px" }}>
                                <label style={{ fontSize: "12px", fontWeight: "700", color: "#1e40af", display: "flex", alignItems: "center", gap: "6px" }}>
                                  Select Target Manager ({group.departmentName} Access) *
                                </label>

                                {/* CUSTOM SINGLE FLOATING DROPDOWN FOR THIS DEPARTMENT */}
                                {(() => {
                                  const validMgrs = (group.availableManagers || []).filter((m) => {
                                    const rCode = String(m.roleCode || m.role_code || "").toUpperCase();
                                    const rName = String(m.roleName || m.role_name || "").toLowerCase();
                                    const isLowest = rCode === "TEAM_MEMBER" || rCode === "TM" || rName.includes("team member");
                                    return !isLowest;
                                  });

                                  const selectedMgr = validMgrs.find(
                                    (m) => String(m.userId || m.user_id) === String(groupTargetManagerId)
                                  ) || validMgrs[0];

                                  const isOpen = openConflictDropdownIdx === grpIdx;

                                  return (
                                    <div style={{ position: "relative" }}>
                                      <div
                                        onClick={() => setOpenConflictDropdownIdx(isOpen ? null : grpIdx)}
                                        style={{
                                          padding: "10px 14px",
                                          background: "#eff6ff",
                                          border: `1.5px solid ${isOpen ? "#1d4ed8" : "#3b82f6"}`,
                                          borderRadius: "10px",
                                          cursor: "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          boxShadow: "0 2px 6px rgba(37, 99, 235, 0.08)",
                                          transition: "all 0.2s ease",
                                        }}
                                      >
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                          <div
                                            style={{
                                              width: "28px",
                                              height: "28px",
                                              borderRadius: "50%",
                                              background: "#2563eb",
                                              color: "#ffffff",
                                              fontSize: "11px",
                                              fontWeight: "800",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                            }}
                                          >
                                            {(selectedMgr?.firstName?.[0] || selectedMgr?.first_name?.[0] || "M") +
                                              (selectedMgr?.lastName?.[0] || selectedMgr?.last_name?.[0] || "")}
                                          </div>
                                          <div style={{ display: "flex", flexDirection: "column" }}>
                                            <span style={{ fontSize: "13px", fontWeight: "800", color: "#1e40af" }}>
                                              {selectedMgr?.firstName || selectedMgr?.first_name} {selectedMgr?.lastName || selectedMgr?.last_name}
                                            </span>
                                            <span style={{ fontSize: "11px", color: "#3b82f6", fontWeight: "600" }}>
                                              {selectedMgr?.email || selectedMgr?.email_address}
                                            </span>
                                          </div>
                                        </div>
                                        <ChevronDown
                                          size={18}
                                          color="#2563eb"
                                          style={{
                                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                            transition: "transform 0.2s ease",
                                          }}
                                        />
                                      </div>

                                      {/* FLOATING OPTIONS POPUP MENU */}
                                      {isOpen && (
                                        <div
                                          style={{
                                            position: "absolute",
                                            top: "calc(100% + 4px)",
                                            left: 0,
                                            right: 0,
                                            background: "#ffffff",
                                            border: "1.5px solid #bfdbfe",
                                            borderRadius: "12px",
                                            boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18)",
                                            zIndex: 999999,
                                            maxHeight: "220px",
                                            overflowY: "auto",
                                            padding: "4px",
                                          }}
                                        >
                                          {validMgrs.map((m) => {
                                            const mId = String(m.userId || m.user_id);
                                            const isSel = mId === String(groupTargetManagerId);
                                            const mName = `${m.firstName || m.first_name || ""} ${m.lastName || m.last_name || ""}`.trim();
                                            const mEmail = m.email || m.email_address || "";
                                            const initials = (m.firstName?.[0] || m.first_name?.[0] || "M") + (m.lastName?.[0] || m.last_name?.[0] || "");

                                            return (
                                              <div
                                                key={mId}
                                                onClick={() => {
                                                  const updated = [...deptConflictData.affectedSubordinates];
                                                  group.subEntries.forEach(({ originalIdx }) => {
                                                    updated[originalIdx].selectedAction = "move";
                                                    updated[originalIdx].selectedTargetManagerId = mId;
                                                  });
                                                  setDeptConflictData({ ...deptConflictData, affectedSubordinates: updated });
                                                  setOpenConflictDropdownIdx(null);
                                                }}
                                                style={{
                                                  padding: "9px 12px",
                                                  cursor: "pointer",
                                                  background: isSel ? "#eff6ff" : "#ffffff",
                                                  borderRadius: "8px",
                                                  marginBottom: "2px",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "space-between",
                                                  transition: "background 0.15s ease",
                                                }}
                                                onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = "#f8fafc"; }}
                                                onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = "#ffffff"; }}
                                              >
                                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                  <div
                                                    style={{
                                                      width: "26px",
                                                      height: "26px",
                                                      borderRadius: "50%",
                                                      background: isSel ? "#2563eb" : "#cbd5e1",
                                                      color: "#ffffff",
                                                      fontSize: "10.5px",
                                                      fontWeight: "800",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                    }}
                                                  >
                                                    {initials}
                                                  </div>
                                                  <div style={{ display: "flex", flexDirection: "column" }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                      <span style={{ fontSize: "12.5px", fontWeight: "800", color: isSel ? "#1d4ed8" : "#0f172a" }}>
                                                        {mName}
                                                      </span>
                                                      {(m.roleName || m.role_name) && (
                                                        <span style={{ background: "#eff6ff", color: "#1d4ed8", padding: "1px 6px", borderRadius: "6px", fontSize: "9.5px", fontWeight: "800", border: "1px solid #bfdbfe" }}>
                                                          {m.roleName || m.role_name}
                                                        </span>
                                                      )}
                                                    </div>
                                                    <span style={{ fontSize: "11px", color: isSel ? "#2563eb" : "#64748b" }}>
                                                      {mEmail}
                                                    </span>
                                                  </div>
                                                </div>
                                                {isSel && <Check size={16} color="#2563eb" />}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            ) : (
                              <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", padding: "10px 12px", borderRadius: "10px", fontSize: "12px", color: "#991b1b", lineHeight: "1.45" }}>
                                ⚠️ <strong>Remove & Disconnect:</strong> All {group.subEntries.length} subordinate member(s) will be disconnected from {deptConflictData.managerUser?.firstName || deptConflictData.managerUser?.first_name} and set free with cleared department access so they can be re-assigned later.
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", padding: "12px", borderRadius: "10px", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <div style={{ fontSize: "12.5px", fontWeight: "800", color: "#991b1b", display: "flex", alignItems: "center", gap: "6px" }}>
                              <AlertCircle size={16} color="#dc2626" /> No Manager Available in Organization
                            </div>
                            <div style={{ fontSize: "12px", color: "#7f1d1d", lineHeight: "1.4" }}>
                              No other manager in the organization has access to <strong>{group.departmentName}</strong> department. Option to move is disabled; members must be disconnected.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            <div style={s.modalFooter}>
              <button
                type="button"
                style={s.cancelBtn}
                onClick={handleCancelDeptConflict}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ ...s.saveBtn, background: "#dc2626" }}
                onClick={handleResolveDeptConflictSubmit}
                disabled={submitting}
              >
                {submitting ? "Resolving..." : "Confirm & Save Department Access"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL 4: ADD / EDIT HIERARCHY ROLE ================= */}
      {showAddRoleModal && (
        <div style={s.modalOverlay} onClick={() => setShowAddRoleModal(false)}>
          <div style={s.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{editingRoleId ? "Edit Dynamic Hierarchy Role" : "Add New Dynamic Hierarchy Role"}</h3>
              <button style={s.closeBtn} onClick={() => setShowAddRoleModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreateRole} style={s.modalBody}>
              <div style={s.formGroup}>
                <label style={s.label}>Role Name *</label>
                <input
                  style={s.input}
                  placeholder="e.g. Vice President, Team Lead, Senior Director"
                  value={newRoleName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewRoleName(val);
                    if (!editingRoleId) {
                      setNewRoleCode(val.trim().toUpperCase().replace(/\s+/g, "_"));
                    }
                  }}
                />
              </div>

              <div style={s.formGroup}>
                <label style={s.label}>Role Code * (Required & Unique)</label>
                <input
                  style={s.input}
                  placeholder="e.g. VP, TL, SR_DIR"
                  value={newRoleCode}
                  onChange={(e) => setNewRoleCode(e.target.value.toUpperCase().replace(/\s+/g, "_"))}
                />
              </div>

              <div style={{ ...s.formGroup, position: "relative" }}>
                <label style={s.label}>Reports To Role (Parent Superior Role)</label>
                {(() => {
                  // Build top-down tree order ONLY for connected primary hierarchy roles (EXCLUDE independent standalone roles!)
                  const roleMap = new Map();
                  hierarchyRoles.forEach((r) => roleMap.set(r.roleId, { ...r, subRoles: [] }));

                  const roots = [];
                  roleMap.forEach((r) => {
                    if (r.parentRoleId && roleMap.has(r.parentRoleId)) {
                      roleMap.get(r.parentRoleId).subRoles.push(r);
                    } else {
                      roots.push(r);
                    }
                  });

                  const options = [];
                  const traverse = (node, depth = 0) => {
                    if (options.some((o) => o.roleId === node.roleId)) return;
                    options.push({ ...node, depth });
                    (node.subRoles || []).forEach((child) => traverse(child, depth + 1));
                  };

                  // Traverse all roots (including independent unattached roles)
                  roots.forEach((root) => traverse(root, 0));

                  const forbiddenIds = new Set();
                  if (editingRoleId) {
                    const eId = parseInt(editingRoleId, 10);
                    forbiddenIds.add(eId);
                    const collectDescendants = (parentId) => {
                      hierarchyRoles.forEach((r) => {
                        if (r.parentRoleId && parseInt(r.parentRoleId, 10) === parentId) {
                          if (!forbiddenIds.has(r.roleId)) {
                            forbiddenIds.add(r.roleId);
                            collectDescendants(r.roleId);
                          }
                        }
                      });
                    };
                    collectDescendants(eId);
                  }

                  const isLowestRole = (r) => {
                    const code = (r.roleCode || "").toUpperCase();
                    const name = (r.roleName || "").toLowerCase();
                    return code === "TEAM_MEMBER" || code === "TM" || name.includes("team member");
                  };

                  const validOptions = options.filter((r) => {
                    if (forbiddenIds.has(r.roleId)) return false;
                    if (isLowestRole(r)) return false; // Lowest hierarchy level cannot be a parent!
                    return true; // Include independent roles!
                  });
                  const selectedParentRoleObj = hierarchyRoles.find((r) => String(r.roleId) === String(newRoleParentId));

                  return (
                    <div style={{ position: "relative", width: "100%" }}>
                      {/* SLEEK TRIGGER BOX */}
                      <div
                        onClick={() => setIsParentRoleDropdownOpen(!isParentRoleDropdownOpen)}
                        style={{
                          width: "100%",
                          minHeight: "42px",
                          padding: "8px 12px",
                          background: "#ffffff",
                          border: isParentRoleDropdownOpen ? "2px solid #2563eb" : "1.5px solid #cbd5e1",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          boxShadow: isParentRoleDropdownOpen ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
                          boxSizing: "border-box",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Network size={16} color="#2563eb" />
                          <span style={{ fontSize: "13px", fontWeight: "700", color: selectedParentRoleObj ? "#0f172a" : "#64748b" }}>
                            {selectedParentRoleObj
                              ? `${selectedParentRoleObj.roleName} [${selectedParentRoleObj.roleCode}]`
                              : "-- Top Level Role (No Parent Superior) --"}
                          </span>
                        </div>
                        <ChevronDown
                          size={16}
                          color="#64748b"
                          style={{
                            transform: isParentRoleDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </div>

                      {/* FLOATING CUSTOM DROPDOWN POPUP MENU */}
                      {isParentRoleDropdownOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 4px)",
                            left: 0,
                            right: 0,
                            background: "#ffffff",
                            border: "1.5px solid #e2e8f0",
                            borderRadius: "12px",
                            boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18)",
                            zIndex: 99999,
                            maxHeight: "260px",
                            overflowY: "auto",
                          }}
                        >
                          {/* TOP LEVEL OPTION (ONLY ENABLED IF NO TOP LEVEL ROOT EXISTS YET) */}
                          {(() => {
                            const existingTopRoot = hierarchyRoles.find((r) => !r.parentRoleId);

                            return (
                              <div
                                onClick={() => {
                                  setNewRoleParentId("");
                                  setIsParentRoleDropdownOpen(false);
                                }}
                                style={{
                                  padding: "10px 14px",
                                  cursor: "pointer",
                                  background: !newRoleParentId ? "#eff6ff" : "#ffffff",
                                  borderBottom: "1px solid #f1f5f9",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div>
                                  <div style={{ fontSize: "12.5px", fontWeight: "800", color: "#0f172a" }}>
                                    🌐 -- Top Level Role (No Parent Superior) --
                                  </div>
                                  <div style={{ fontSize: "11px", color: "#64748b" }}>
                                    {existingTopRoot
                                      ? `Creates new top-level root role (re-parents '${existingTopRoot.roleName}' under new role)`
                                      : "Top-level root role"}
                                  </div>
                                </div>
                                {!newRoleParentId && <Check size={14} color="#2563eb" />}
                              </div>
                            );
                          })()}

                          {/* TREE ROLE OPTIONS */}
                          {validOptions.map((r) => {
                            const isSelected = String(newRoleParentId) === String(r.roleId);
                            return (
                              <div
                                key={r.roleId}
                                onClick={() => {
                                  setNewRoleParentId(String(r.roleId));
                                  setIsParentRoleDropdownOpen(false);
                                }}
                                style={{
                                  padding: "10px 14px",
                                  paddingLeft: `${14 + r.depth * 18}px`,
                                  cursor: "pointer",
                                  background: isSelected ? "#eff6ff" : "#ffffff",
                                  borderBottom: "1px solid #f8fafc",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  {r.depth > 0 && <span style={{ color: "#2563eb", fontWeight: "800", fontSize: "12px" }}>└──</span>}
                                  <div>
                                    <div style={{ fontSize: "12.5px", fontWeight: "800", color: isSelected ? "#1d4ed8" : "#0f172a" }}>
                                      {r.roleName} <span style={{ color: "#2563eb", fontSize: "11px", fontWeight: "700" }}>[{r.roleCode}]</span>
                                    </div>
                                    <div style={{ fontSize: "10.5px", color: "#64748b" }}>
                                      {!r.parentRoleId && (!r.subRoles || r.subRoles.length === 0)
                                        ? "Independent Role (Unattached)"
                                        : r.parentRoleName
                                          ? `Reports to ${r.parentRoleName}`
                                          : "Top Level Role"}
                                    </div>
                                  </div>
                                </div>
                                {isSelected && <Check size={14} color="#2563eb" />}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", padding: "12px 14px", borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "700", color: "#1e293b" }}>Allow Multi-Department Assignment?</div>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>Permit user with this role to belong to multiple departments</div>
                </div>
                <input
                  type="checkbox"
                  style={{ width: "18px", height: "18px", cursor: "pointer" }}
                  checked={newRoleMultiDept}
                  onChange={(e) => setNewRoleMultiDept(e.target.checked)}
                />
              </div> */}

              <div style={s.modalFooter}>
                <button type="button" style={s.cancelBtn} onClick={() => setShowAddRoleModal(false)}>Cancel</button>
                <button type="submit" style={s.saveBtn} disabled={submitting}>
                  {submitting ? "Saving..." : "+ Create Dynamic Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= DEPARTMENT MEMBERS POP-UP MODAL ================= */}
      {showDeptMembersModal && selectedDeptForMembers && (
        <div style={s.modalOverlay} onClick={() => setShowDeptMembersModal(false)}>
          <div
            style={{
              ...s.modalBox,
              maxWidth: "680px",
              maxHeight: "85vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header - Solid Blue Background with White Text & Icon */}
            <div style={{ ...s.modalHeader, background: "#2563eb", color: "#ffffff", borderBottom: "none", padding: "18px 24px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ background: "rgba(255, 255, 255, 0.2)", padding: "10px", borderRadius: "10px", display: "flex", border: "1px solid rgba(255, 255, 255, 0.3)" }}>
                  <Building2 size={20} color="#ffffff" />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "800", color: "#ffffff" }}>
                    {selectedDeptForMembers.departmentName || selectedDeptForMembers.department_name}
                  </h3>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "3px" }}>
                    <span style={{ background: "rgba(255, 255, 255, 0.25)", color: "#ffffff", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "700" }}>
                      [{selectedDeptForMembers.departmentCode || selectedDeptForMembers.department_code}]
                    </span>
                    <span style={{ color: "#dbeafe", fontSize: "12.5px", fontWeight: "500" }}>
                      {deptMembersList.length} Active {deptMembersList.length === 1 ? "Member" : "Members"}
                    </span>
                  </div>
                </div>
              </div>
              <button
                style={{ background: "rgba(255, 255, 255, 0.2)", border: "none", color: "#ffffff", cursor: "pointer", width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s ease" }}
                onClick={() => setShowDeptMembersModal(false)}
              >
                <X size={18} color="#ffffff" />
              </button>
            </div>

            {/* Modal Search Bar */}
            <div style={{ padding: "14px 24px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "#ffffff", padding: "0 14px", borderRadius: "10px", border: "1px solid #cbd5e1", height: "40px" }}>
                <Search size={16} color="#64748b" />
                <input
                  placeholder="Search members by name, email, or designation..."
                  value={deptMemberSearchQuery}
                  onChange={(e) => setDeptMemberSearchQuery(e.target.value)}
                  style={{ border: "none", background: "transparent", outline: "none", width: "100%", fontSize: "13px", fontWeight: "600", color: "#0f172a" }}
                />
                {deptMemberSearchQuery && (
                  <button
                    onClick={() => setDeptMemberSearchQuery("")}
                    style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: 0 }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: "12px", minHeight: "260px" }}>
              {loadingDeptMembers ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0", color: "#64748b", gap: "12px" }}>
                  <div style={{ width: "28px", height: "28px", border: "3px solid #cbd5e1", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  <span style={{ fontSize: "13px", fontWeight: "600" }}>Loading department members...</span>
                </div>
              ) : (() => {
                const filtered = deptMembersList.filter((m) => {
                  if (!deptMemberSearchQuery.trim()) return true;
                  const q = deptMemberSearchQuery.toLowerCase().trim();
                  const fullName = `${m.firstName || m.first_name || ""} ${m.lastName || m.last_name || ""}`.toLowerCase();
                  const email = (m.email || m.email_address || "").toLowerCase();
                  const designation = (m.designation || m.roleName || "").toLowerCase();
                  return fullName.includes(q) || email.includes(q) || designation.includes(q);
                });

                if (filtered.length === 0) {
                  return (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 20px", textAlignment: "center", color: "#64748b", gap: "10px", background: "#f8fafc", borderRadius: "14px", border: "1px dashed #cbd5e1" }}>
                      <Users size={36} color="#94a3b8" />
                      <div style={{ fontSize: "15px", fontWeight: "700", color: "#1e293b" }}>
                        {deptMembersList.length === 0 ? "No Members Assigned" : "No Matching Members Found"}
                      </div>
                      <div style={{ fontSize: "12.5px", color: "#64748b", maxWidth: "340px", textAlign: "center" }}>
                        {deptMembersList.length === 0
                          ? `No users are currently assigned to the ${selectedDeptForMembers.departmentName || selectedDeptForMembers.department_name} department.`
                          : `No members matched "${deptMemberSearchQuery}". Try clearing your search.`}
                      </div>
                    </div>
                  );
                }

                return filtered.map((member, idx) => {
                  const fullName = `${member.firstName || member.first_name || ""} ${member.lastName || member.last_name || ""}`.trim() || member.email || "User";
                  const desigLower = (member.designation || member.roleName || "").toLowerCase();
                  const isMgr = desigLower.includes("manager") || desigLower.includes("lead") || desigLower.includes("vp") || desigLower.includes("head");

                  return (
                    <div
                      key={member.userId || member.user_id || idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "14px 16px",
                        background: "#ffffff",
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        gap: "14px",
                      }}
                    >
                      {/* Left: Simple Blue/Slate Avatar & User Info */}
                      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <div
                          style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "10px",
                            background: "#eff6ff",
                            color: "#2563eb",
                            border: "1px solid #dbeafe",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "800",
                            flexShrink: 0,
                          }}
                        >
                          {getInitials(fullName)}
                        </div>
                        <div>
                          <div style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                            <span>{fullName}</span>
                            <span style={s.activeBadge}><CheckCircle2 size={11} style={{ marginRight: 3 }} /> Active</span>
                          </div>
                          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                            {member.email || member.email_address}
                          </div>
                          {member.reportingToName && (
                            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                              <Network size={11} color="#64748b" />
                              <span>Reports to: <strong style={{ color: "#334155" }}>{member.reportingToName}</strong></span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right: Simple Clean Designation Badge */}
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "4.5px 11px",
                            borderRadius: "7px",
                            background: isMgr ? "#f0fdf4" : "#f8fafc",
                            color: isMgr ? "#166534" : "#334155",
                            border: `1px solid ${isMgr ? "#bbf7d0" : "#cbd5e1"}`,
                            fontSize: "12px",
                            fontWeight: "700",
                          }}
                        >
                          <ShieldCheck size={13} color={isMgr ? "#166534" : "#475569"} />
                          <span>{member.designation || member.roleName || "Team Member"}</span>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Modal Footer */}
            <div style={s.modalFooter}>
              <button
                style={s.cancelBtn}
                onClick={() => setShowDeptMembersModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", background: "#f8fafc" },
  header: { marginBottom: "12px" },
  breadcrumb: { fontSize: "11.5px", color: "#94a3b8", marginBottom: "3px" },
  title: { margin: 0, fontSize: "24px", fontWeight: "800", color: "#0f172a", letterSpacing: "-0.5px" },
  sub: { color: "#64748b", marginTop: "3px", fontSize: "13px", fontWeight: "500" },
  backLink: { display: "inline-flex", alignItems: "center", fontSize: "12.5px", color: "#2563eb", cursor: "pointer", fontWeight: "700", background: "none", border: "none", padding: 0, marginBottom: "4px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "20px", marginTop: "10px" },
  menuCard: { background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "20px", cursor: "pointer", transition: "all 0.2s ease-in-out", boxShadow: "0 4px 12px rgba(15, 23, 42, 0.03)" },
  cardTitle: { margin: "12px 0 0 0", fontSize: "16px", fontWeight: "800", color: "#0f172a" },
  cardDesc: { marginTop: "6px", fontSize: "13px", color: "#64748b", lineHeight: "1.4" },
  iconBox: { background: "#f1f5f9", padding: "10px", borderRadius: "12px", display: "inline-flex" },
  activeBadge: { display: "inline-flex", alignItems: "center", padding: "3px 9px", background: "#f0fdf4", color: "#166534", borderRadius: "20px", fontSize: "11.5px", fontWeight: "700", border: "1px solid #bbf7d0" },
  deptCodeBadge: { display: "inline-block", padding: "2px 8px", background: "#f1f5f9", color: "#475569", borderRadius: "6px", fontSize: "11px", fontWeight: "700", marginTop: "6px" },
  primaryBtn: { padding: "8.5px 16px", background: "#2563eb", color: "#ffffff", border: "none", borderRadius: "8px", fontWeight: "700", fontSize: "13px", cursor: "pointer", boxShadow: "0 2px 8px rgba(37,99,235,0.2)" },
  refreshBtn: { padding: "8.5px 16px", background: "#ffffff", color: "#334155", border: "1.5px solid #cbd5e1", borderRadius: "8px", fontWeight: "700", fontSize: "13px", cursor: "pointer" },
  detailCard: { position: "fixed", bottom: "24px", right: "24px", background: "#ffffff", border: "2px solid #2563eb", borderRadius: "16px", padding: "20px", width: "320px", boxShadow: "0 20px 40px rgba(15, 23, 42, 0.18)", zIndex: 999 },
  toast: { padding: "12px 16px", borderRadius: "10px", marginBottom: "16px", fontSize: "13px", fontWeight: "700", boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999999, padding: "20px", boxSizing: "border-box" },
  modalBox: { background: "#ffffff", borderRadius: "16px", width: "100%", maxWidth: "500px", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 48px rgba(15, 23, 42, 0.25)" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #e2e8f0", flexShrink: 0 },
  modalTitle: { fontSize: "16px", fontWeight: "800", color: "#0f172a", margin: 0 },
  closeBtn: { background: "none", border: "none", color: "#64748b", cursor: "pointer" },
  modalBody: { padding: "20px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column" },
  formGroup: { marginBottom: "14px" },
  label: { display: "block", fontSize: "12.5px", fontWeight: "700", color: "#334155", marginBottom: "5px" },
  input: { width: "100%", height: "40px", padding: "0 12px", background: "#ffffff", border: "1.5px solid #cbd5e1", borderRadius: "8px", fontSize: "13px", fontWeight: "600", color: "#0f172a", outline: "none", boxSizing: "border-box" },
  modalFooter: { display: "flex", justifyContent: "flex-end", gap: "10px", padding: "16px 20px 20px 20px", borderTop: "1px solid #f1f5f9", background: "#f8fafc", borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px", flexShrink: 0 },
  cancelBtn: { padding: "8.5px 16px", background: "#ffffff", border: "1.5px solid #cbd5e1", borderRadius: "8px", fontSize: "13px", fontWeight: "700", color: "#475569", cursor: "pointer" },
  saveBtn: { padding: "8.5px 18px", background: "#2563eb", color: "#ffffff", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "700", cursor: "pointer", boxShadow: "0 2px 8px rgba(37,99,235,0.2)" },
  dangerBtn: { padding: "8.5px 18px", background: "#dc2626", color: "#ffffff", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "700", cursor: "pointer", boxShadow: "0 2px 8px rgba(220,38,38,0.2)" },
};
